import { useState, useEffect, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const PAGE_BG = 'radial-gradient(circle at top, #1186c5 0%, #0b3b57 35%, #082c40 100%)';

export default function WaterConservation() {
  const customer = JSON.parse(localStorage.getItem('customer') || '{}');
  const customerId = customer.id;

  const [status, setStatus] = useState(null);
  const [screen, setScreen] = useState('overview');

  const fetchStatus = useCallback(async () => {
    if (!customerId) return;
    const res = await fetch(`${BACKEND_URL}/api/watersmart/dashboard/${customerId}`);
    setStatus(await res.json());
  }, [customerId]);

  useEffect(() => { fetchStatus(); }, [fetchStatus]);

  if (status === null) {
    return <div style={{ minHeight: '100vh', background: PAGE_BG }} />;
  }

  if (!status.connected) {
    return <ConnectScreen customerId={customerId} onConnected={fetchStatus} />;
  }

  return (
    <div style={{ minHeight: '100vh', background: PAGE_BG, position: 'relative' }}>
      <div style={{ position: 'absolute', inset: 0, overflow: 'hidden', pointerEvents: 'none' }}>
        <div style={{ position: 'absolute', left: '-8%', top: '12%', height: 520, width: 520, borderRadius: '50%', background: 'rgba(34,211,238,0.10)', filter: 'blur(64px)' }} />
        <div style={{ position: 'absolute', right: '-10%', top: '8%', height: 520, width: 520, borderRadius: '50%', background: 'rgba(52,211,153,0.10)', filter: 'blur(64px)' }} />
      </div>
      <div style={{ position: 'relative', maxWidth: 1280, margin: '0 auto', padding: '32px 24px 48px', boxSizing: 'border-box' }}>
        <PortalNav screen={screen} onNavigate={setScreen} />
        {screen === 'overview' && <OverviewTab customerId={customerId} />}
        {screen === 'controls' && <ControlsTab customerId={customerId} />}
        {screen === 'usage' && <UsageTab customerId={customerId} />}
        {screen === 'history' && <HistoryTab customerId={customerId} />}
        {screen === 'alerts' && <AlertsTab customerId={customerId} />}
        {screen === 'settings' && <SettingsTab customerId={customerId} onDisconnect={fetchStatus} />}
        <Footer />
      </div>
    </div>
  );
}

const NAV_TABS = [
  ['overview', 'Overview'], ['controls', 'Controls'], ['usage', 'Usage'],
  ['history', 'History'], ['alerts', 'Alerts'], ['settings', 'Settings'],
];

function PortalNav({ screen, onNavigate }) {
  const customer = JSON.parse(localStorage.getItem('customer') || '{}');

  return (
    <>
      <div
        style={{
          marginBottom: 24,
          display: 'flex',
          flexWrap: 'wrap',
          gap: 20,
          alignItems: 'center',
          justifyContent: 'space-between',
          borderRadius: 28,
          border: '1px solid rgba(255,255,255,0.1)',
          background: 'rgba(2,6,23,0.45)',
          padding: 24,
          boxShadow: '0 25px 50px rgba(0,0,0,0.25)',
          backdropFilter: 'blur(24px)',
          boxSizing: 'border-box',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          <div
            style={{
              display: 'flex',
              height: 64,
              width: 64,
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: '50%',
              border: '1px solid rgba(103,232,249,0.2)',
              background: 'rgba(255,255,255,0.05)',
              flexShrink: 0,
            }}
          >
            <img
              src="/logo.png"
              alt="AAA Irrigation Service logo"
              style={{ height: 56, width: 56, borderRadius: '50%', objectFit: 'cover' }}
            />
          </div>
          <div>
            <div style={SECTION_LABEL_STYLE}>AAA Irrigation Service</div>
            <div style={{ marginTop: 4, fontSize: 28, fontWeight: 600, letterSpacing: '-0.02em', color: '#ffffff' }}>
              Customer Dashboard
            </div>
            <div style={{ marginTop: 4, fontSize: 14, color: '#cbd5e1' }}>
              Welcome back, {customer.name || 'there'}
            </div>
          </div>
        </div>
        <Link
          to="/dashboard"
          style={{
            borderRadius: 16,
            border: '1px solid rgba(255,255,255,0.1)',
            background: 'rgba(255,255,255,0.05)',
            padding: '12px 16px',
            fontSize: 14,
            fontWeight: 500,
            color: '#f1f5f9',
            textDecoration: 'none',
            whiteSpace: 'nowrap',
          }}
        >
          ← Back to Dashboard
        </Link>
      </div>

      <div
        style={{
          display: 'flex',
          borderRadius: 12,
          border: '1px solid rgba(255,255,255,0.1)',
          overflow: 'hidden',
          width: 'fit-content',
          maxWidth: '100%',
          overflowX: 'auto',
        }}
      >
        {NAV_TABS.map(([id, label]) => {
          const active = screen === id;
          return (
            <div
              key={id}
              role="button"
              tabIndex={0}
              onClick={() => onNavigate(id)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') onNavigate(id);
              }}
              style={{
                padding: '10px 20px',
                fontSize: 14,
                fontWeight: 600,
                cursor: 'pointer',
                whiteSpace: 'nowrap',
                background: active ? 'rgba(34,211,238,0.2)' : 'rgba(255,255,255,0.05)',
                color: active ? '#cffafe' : '#94a3b8',
              }}
            >
              {label}
            </div>
          );
        })}
      </div>
    </>
  );
}

function Footer() {
  return (
    <div style={{ marginTop: 40, textAlign: 'center', fontSize: 12, color: 'rgba(255,255,255,0.4)' }}>
      AAA Irrigation Service · LI0016993 · (469) 751-3567 ·{' '}
      <span style={{ color: '#00e0b8', fontWeight: 600 }}>Powered by Boatman Systems™</span>
    </div>
  );
}

const CARD_STYLE = {
  borderRadius: 28,
  border: '1px solid rgba(255,255,255,0.1)',
  background: 'rgba(2,6,23,0.45)',
  padding: 24,
  boxShadow: '0 25px 50px rgba(0,0,0,0.25)',
  backdropFilter: 'blur(24px)',
  boxSizing: 'border-box',
};

const SECTION_LABEL_STYLE = {
  fontSize: 12,
  fontWeight: 600,
  textTransform: 'uppercase',
  letterSpacing: '0.35em',
  color: 'rgba(165,243,252,0.7)',
};

function zoneStatusStyle(status) {
  if (status === 'Healthy') {
    return { bg: 'rgba(52,211,153,0.1)', color: '#a7f3d0' };
  }
  if (status === 'Disabled') {
    return { bg: 'rgba(255,255,255,0.08)', color: '#94a3b8' };
  }
  return { bg: 'rgba(255,255,255,0.08)', color: '#cbd5e1' };
}

function NotLinkedMessage() {
  return (
    <div style={{ ...CARD_STYLE, marginTop: 24, textAlign: 'center' }}>
      <div style={{ fontSize: 15, fontWeight: 600, color: '#ffffff' }}>No irrigation controller linked yet</div>
      <div style={{ marginTop: 8, fontSize: 14, color: '#94a3b8', lineHeight: 1.5 }}>
        Connect a Rachio controller to see zone status and run zones from the portal.
      </div>
    </div>
  );
}

function OverviewTab({ customerId }) {
  const [rachio, setRachio] = useState(null);
  const [waterSummary, setWaterSummary] = useState(null);
  // Independent fetch of the same Task-10 zone-metrics endpoint the Controls
  // tab, Alerts tab, and History tab's Zone GPM sub-tab each fetch on their
  // own (no shared cache — matches this file's existing convention). Used
  // for the no-flow anomaly banner below AND (as of the in/hr->gallons fix)
  // for the zone table's Flow column — real metered GPM x scheduled runtime
  // minutes, same "flowRow" pattern ControlsTab already uses via its own
  // zoneMetricsFor, since Rachio's own zone.flow ("nozzle inchesPerHour")
  // is an application-rate unit, not gallons, and this page is gallon-based
  // everywhere else (Water Used, Tier Status, etc).
  const [zoneMetrics, setZoneMetrics] = useState(null);
  // Billing/leak/savings — recovered v2 stat cards (Water Used This Cycle,
  // Tier Status, Leak Watch, Saved This Year). Added alongside (not
  // replacing) the newer Zones Healthy/Water Used/Readings Synced cards
  // sourced from the /dashboard summary endpoint — the two card sets read
  // from different endpoints and surface different info (billing-cycle
  // tier/cost position + leak detection + cumulative savings vs. raw recent
  // sync totals), so they're additive, not a redesign conflict. Yes, "Water
  // Used" and "Water Used This Cycle" sit next to each other and overlap in
  // wording — kept both verbatim per instruction not to drop anything;
  // flagged in the tracker as a candidate for a follow-up copy pass, not
  // resolved here by unilaterally cutting one.
  const [billing, setBilling] = useState(null);
  const [leak, setLeak] = useState(null);
  const [savings, setSavings] = useState(null);

  useEffect(() => {
    if (!customerId) return;
    fetch(`${BACKEND_URL}/api/rachio/dashboard/${customerId}`).then((r) => r.json()).then(setRachio);
    fetch(`${BACKEND_URL}/api/watersmart/dashboard/${customerId}`).then((r) => r.json()).then(setWaterSummary);
    fetch(`${BACKEND_URL}/api/watersmart/zone-metrics/${customerId}`).then((r) => r.json()).then(setZoneMetrics);
    fetch(`${BACKEND_URL}/api/watersmart/billing/${customerId}`).then((r) => r.json()).then(setBilling);
    fetch(`${BACKEND_URL}/api/watersmart/leak-status/${customerId}`).then((r) => r.json()).then(setLeak);
    fetch(`${BACKEND_URL}/api/watersmart/savings/${customerId}`).then((r) => r.json()).then(setSavings);
  }, [customerId]);

  if (!rachio) return null;

  if (rachio.supported === false || !rachio.controller) {
    return <NotLinkedMessage />;
  }

  const { controller, zones: allZones = [], zone_count: zoneCount, healthy_count: healthyCount, schedules = [] } = rachio;
  // Disabled zones (e.g. unused/capped stations) clutter this table and
  // aren't part of the system the customer is actually running — zoneCount/
  // healthyCount from the backend already exclude them (see
  // backend/server.py _build_device_summary's enabled_zones), so the
  // visible list should match rather than showing every raw Rachio zone.
  const zones = allZones.filter((z) => z.enabled);
  const summary = waterSummary && waterSummary.synced ? waterSummary.summary : null;
  const leakActive = leak && leak.detected;

  // Real measured GPM keyed by zone number (falls back to zone name) — same
  // pattern as ControlsTab's zoneMetricsFor (see its comment above for the
  // reasoning). Only shown once WaterSmart has enough metered history.
  const zoneMetricsByKey = {};
  (zoneMetrics?.rows || []).forEach((row) => {
    const key = row.zone_number != null ? `n:${row.zone_number}` : `s:${row.zone_name}`;
    zoneMetricsByKey[key] = row;
  });
  const zoneMetricsFor = (z) => {
    if (z.number != null && zoneMetricsByKey[`n:${z.number}`]) return zoneMetricsByKey[`n:${z.number}`];
    return zoneMetricsByKey[`s:${z.name}`] || null;
  };

  const stats = [
    { label: 'Zones Healthy', value: `${healthyCount ?? 0}/${zoneCount ?? zones.length}`, sub: 'Rachio zone health' },
    {
      label: 'Water Used',
      value: summary && summary.total != null ? `${Math.round(summary.total).toLocaleString()} gal` : '—',
      sub: 'Recent smart-meter readings',
    },
    {
      label: 'Readings Synced',
      value: summary && summary.count != null ? summary.count.toLocaleString() : '—',
      sub: 'WaterSmart sync history',
    },
    {
      label: 'Water Used This Cycle',
      value: billing && billing.available ? `${Math.round(billing.usage_to_date).toLocaleString()} gal` : '—',
      sub: billing && billing.available ? `$${billing.cost_to_date.toFixed(2)} so far · ${billing.cycle_days_left} days left` : 'Awaiting first sync',
    },
    {
      label: 'Tier Status',
      value: billing && billing.available
        ? (billing.usage_to_date < billing.tier_breakpoint_gal ? 'Tier 1–2' : 'Tier 3+') : '—',
      sub: billing && billing.available
        ? (billing.usage_to_date < billing.tier_breakpoint_gal
          ? `${Math.round(billing.tier_breakpoint_gal - billing.usage_to_date).toLocaleString()} gal to next tier`
          : `${Math.round(billing.usage_to_date - billing.tier_breakpoint_gal).toLocaleString()} gal over Tier 2 threshold`) : 'Awaiting first sync',
    },
    {
      label: 'Leak Watch',
      value: leakActive ? (leak.severity === 'major' ? 'MAJOR LEAK' : 'Minor leak') : 'Clear',
      sub: leakActive ? `~${leak.rate_gal_hr} gal/hr · see Alerts` : 'No continuous flow detected',
      alert: !!leakActive,
    },
    {
      // savings.year.{gal,usd} is 0 whenever no conservation action (Skip
      // Next Run / Pause Sprinklers, Usage tab) has ever been applied — that
      // is the correct, non-fabricated answer for an account that hasn't
      // used the feature yet, not a broken calculation. Framed as an
      // invitation rather than a bare "0 gal" so it doesn't read as a
      // missing/broken feature.
      label: 'Saved This Year',
      value: savings ? `${Math.round(savings.year.gal).toLocaleString()} gal` : '—',
      sub: !savings
        ? '—'
        : savings.year.gal > 0
          ? `$${savings.year.usd.toFixed(2)} in avoided tier charges`
          : 'No savings yet — try Skip/Pause in the Usage tab',
    },
  ];

  return (
    <div style={{ marginTop: 24, display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(340px, 1fr))', gap: 24, alignItems: 'start' }}>
      {zoneMetrics && zoneMetrics.anomalies && zoneMetrics.anomalies.some((a) => a.kind === 'no_flow') && (
        <div style={{
          gridColumn: '1 / -1', borderRadius: 20, padding: '16px 20px',
          background: 'rgba(251,113,133,0.10)', border: '1px solid rgba(251,113,133,0.3)',
        }}>
          <div style={{ fontSize: 15, fontWeight: 700, color: '#fda4af' }}>
            ⚠ Zone not flowing: {zoneMetrics.anomalies.filter((a) => a.kind === 'no_flow').map((a) => a.zone_name).join(', ')}
          </div>
          <div style={{ marginTop: 6, fontSize: 13.5, color: '#cbd5e1' }}>
            A zone ran on schedule but metered almost no water — this usually means a stuck valve. Check the Alerts tab to schedule a service trip.
          </div>
        </div>
      )}
      <div style={{ gridColumn: 'span 1' }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 16, marginBottom: 24 }}>
          {stats.map((st) => (
            <div
              key={st.label}
              style={{
                borderRadius: 24,
                border: st.alert ? '1px solid rgba(251,113,133,0.35)' : '1px solid rgba(103,232,249,0.15)',
                background: st.alert ? 'rgba(251,113,133,0.10)' : 'rgba(34,211,238,0.06)',
                padding: 20,
                boxShadow: '0 20px 40px rgba(0,0,0,0.25)',
                backdropFilter: 'blur(24px)',
                boxSizing: 'border-box',
              }}
            >
              <div style={{ fontSize: 14, fontWeight: 500, color: '#cbd5e1' }}>{st.label}</div>
              <div style={{ marginTop: 12, fontSize: 30, fontWeight: 600, color: st.alert ? '#fda4af' : '#ffffff' }}>{st.value}</div>
              <div style={{ marginTop: 8, fontSize: 14, color: 'rgba(207,250,254,0.75)' }}>{st.sub}</div>
            </div>
          ))}
        </div>

        <div style={CARD_STYLE}>
          <div style={{ marginBottom: 24, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 10 }}>
            <div>
              <div style={SECTION_LABEL_STYLE}>System Overview</div>
              <div style={{ marginTop: 8, fontSize: 24, fontWeight: 600, color: '#ffffff' }}>Irrigation Zones</div>
            </div>
            <div
              style={{
                borderRadius: 999,
                border: '1px solid rgba(110,231,183,0.2)',
                background: 'rgba(52,211,153,0.1)',
                padding: '4px 12px',
                fontSize: 14,
                fontWeight: 500,
                color: '#a7f3d0',
              }}
            >
              {healthyCount ?? 0} of {zoneCount ?? zones.length} healthy
            </div>
          </div>
          <div style={{ overflow: 'hidden', borderRadius: 22, border: '1px solid rgba(255,255,255,0.08)' }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1.6fr 1fr 0.9fr 1fr', background: 'rgba(255,255,255,0.05)', fontSize: 13, color: '#cbd5e1' }}>
              <div style={{ padding: '14px 20px', fontWeight: 500 }}>Zone</div>
              <div style={{ padding: '14px 20px', fontWeight: 500 }}>Status</div>
              <div style={{ padding: '14px 20px', fontWeight: 500 }}>Runtime</div>
              <div style={{ padding: '14px 20px', fontWeight: 500 }}>Flow</div>
            </div>
            {zones.map((z) => {
              const statusStyle = zoneStatusStyle(z.status);
              const flowRow = zoneMetricsFor(z);
              const runtimeMin = parseRuntimeMinutes(z.runtime);
              const flowGal = flowRow?.current_gpm != null && runtimeMin != null
                ? Math.round(flowRow.current_gpm * runtimeMin)
                : null;
              return (
                <div
                  key={z.id}
                  style={{
                    display: 'grid',
                    gridTemplateColumns: '1.6fr 1fr 0.9fr 1fr',
                    borderTop: '1px solid rgba(255,255,255,0.08)',
                    background: 'rgba(15,23,42,0.2)',
                    alignItems: 'center',
                  }}
                >
                  <div style={{ padding: '14px 20px', fontSize: 14, fontWeight: 500, color: '#ffffff' }}>{z.name}</div>
                  <div style={{ padding: '14px 20px' }}>
                    <span
                      style={{
                        borderRadius: 999,
                        padding: '4px 12px',
                        fontSize: 12,
                        fontWeight: 600,
                        background: statusStyle.bg,
                        color: statusStyle.color,
                      }}
                    >
                      {z.status}
                    </span>
                  </div>
                  <div style={{ padding: '14px 20px', fontSize: 14, color: '#e2e8f0' }}>
                    {z.runtime || '—'}
                  </div>
                  <div style={{ padding: '14px 20px', fontSize: 14, color: '#e2e8f0' }}>
                    {flowGal != null ? `${flowGal} gal` : '—'}
                  </div>
                </div>
              );
            })}
          </div>
          <div style={{ marginTop: 24, display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 16 }}>
            <div style={{ borderRadius: 22, border: '1px solid rgba(103,232,249,0.1)', background: 'rgba(34,211,238,0.05)', padding: 20, boxSizing: 'border-box' }}>
              <div style={{ fontSize: 14, fontWeight: 600, color: '#cffafe' }}>Controller Info</div>
              <div style={{ marginTop: 12, display: 'flex', flexDirection: 'column', gap: 8, fontSize: 14, color: '#e2e8f0' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span>Model</span>
                  <span>{controller.model || '—'}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span>Mode</span>
                  <span>{controller.scheduleMode || '—'}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span>Status</span>
                  <span style={{ color: controller.status === 'Healthy' || controller.on ? '#6ee7b7' : '#e2e8f0' }}>
                    {controller.status || '—'}
                  </span>
                </div>
              </div>
            </div>
            <div style={{ borderRadius: 22, border: '1px solid rgba(255,255,255,0.1)', background: 'rgba(255,255,255,0.05)', padding: 20, boxSizing: 'border-box' }}>
              <div style={{ fontSize: 14, fontWeight: 600, color: '#ffffff' }}>Active Schedules</div>
              <div style={{ marginTop: 12, display: 'flex', flexDirection: 'column', gap: 8, fontSize: 14, color: '#e2e8f0' }}>
                {schedules.length === 0 && <div style={{ color: '#94a3b8' }}>No schedules configured.</div>}
                {schedules.map((s) => (
                  <div key={s.id} style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span>{s.name}</span>
                    <span style={{ color: s.enabled ? '#6ee7b7' : '#94a3b8' }}>{s.enabled ? 'Active' : 'Inactive'}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
      <div style={{ gridColumn: 'span 1', display: 'flex', flexDirection: 'column', gap: 16 }}>
        <ReferralCard customerId={customerId} />
      </div>
    </div>
  );
}

function useZonePhoto(zone) {
  const [photo, setPhoto] = useState(zone.imageUrl || null);

  useEffect(() => {
    // Zone photos are owner-or-staff since 2026-10-15: the portal session is attached
    // automatically; in admin mode also send the stored admin key (never prompts here).
    let adminKey = null;
    try { adminKey = sessionStorage.getItem('admin_key'); } catch (e) { /* storage unavailable */ }
    fetch(`${BACKEND_URL}/api/rachio/zone/${zone.id}/photo`, adminKey ? { headers: { 'X-Admin-Key': adminKey } } : undefined)
      .then(r => r.json())
      .then(d => { if (d.photo_data) setPhoto(d.photo_data); })
      .catch(() => {});
  }, [zone.id]);

  const compressImage = (file, callback) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = (event) => {
      const img = new Image();
      img.src = event.target.result;
      img.onload = () => {
        const canvas = document.createElement('canvas');
        let width = img.width;
        let height = img.height;

        // Scale down if needed
        const maxDimension = 1600;
        if (width > maxDimension || height > maxDimension) {
          const ratio = Math.min(maxDimension / width, maxDimension / height);
          width *= ratio;
          height *= ratio;
        }

        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, width, height);

        // Compress until under 3MB
        let quality = 0.75;
        const tryCompress = () => {
          canvas.toBlob(
            (blob) => {
              if (blob.size <= 3_000_000 || quality <= 0.05) {
                const reader2 = new FileReader();
                reader2.onload = (e) => callback(e.target.result);
                reader2.readAsDataURL(blob);
              } else {
                quality -= 0.1;
                tryCompress();
              }
            },
            'image/jpeg',
            quality
          );
        };
        tryCompress();
      };
    };
  };

  const upload = (file, onDone) => {
    if (!file) return;
    if (file.size > 3_000_000) {
      compressImage(file, async (compressedData) => {
        try {
          const res = await fetch(`${BACKEND_URL}/api/rachio/zone/photo`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ zone_id: zone.id, photo_data: compressedData }),
          });
          const data = await res.json();
          if (!res.ok) throw new Error(data.detail || 'Upload failed');
          setPhoto(compressedData);
          onDone && onDone(compressedData);
        } catch (err) { alert('Upload failed: ' + err.message); }
      });
    } else {
      const reader = new FileReader();
      reader.onload = async (ev) => {
        try {
          const res = await fetch(`${BACKEND_URL}/api/rachio/zone/photo`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ zone_id: zone.id, photo_data: ev.target.result }),
          });
          const data = await res.json();
          if (!res.ok) throw new Error(data.detail || 'Upload failed');
          setPhoto(ev.target.result);
          onDone && onDone(ev.target.result);
        } catch (err) { alert('Upload failed: ' + err.message); }
      };
      reader.readAsDataURL(file);
    }
  };

  return { photo, setPhoto, upload };
}

function ZonePhotoSlot({ zone }) {
  const { photo, upload } = useZonePhoto(zone);
  const fileInputRef = useRef(null);

  return (
    <div
      style={{ borderRadius: 16, overflow: 'hidden', width: '100%', height: 140, position: 'relative', cursor: 'pointer', background: 'rgba(255,255,255,0.04)' }}
      onClick={() => fileInputRef.current?.click()}
    >
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        style={{ display: 'none' }}
        onChange={(e) => { if (e.target.files?.[0]) upload(e.target.files[0]); }}
      />
      {photo ? (
        <img src={photo} alt={zone.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
      ) : (
        <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, color: '#64748b' }}>
          Tap to add a zone photo
        </div>
      )}
    </div>
  );
}

// Parses "12 min" -> 12; returns null for "—" or anything unparseable.
function parseRuntimeMinutes(runtime) {
  if (!runtime) return null;
  const m = /^(\d+(?:\.\d+)?)/.exec(runtime);
  return m ? parseFloat(m[1]) : null;
}

function ZoneFlowChip({ status }) {
  if (status === 'no_flow') {
    return (
      <span style={{ fontSize: 10, fontWeight: 700, color: '#fecaca', background: 'rgba(239,68,68,0.15)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: 999, padding: '2px 8px' }}>
        NO FLOW
      </span>
    );
  }
  if (status === 'overage') {
    return (
      <span style={{ fontSize: 10, fontWeight: 700, color: '#fde68a', background: 'rgba(245,158,11,0.15)', border: '1px solid rgba(245,158,11,0.3)', borderRadius: 999, padding: '2px 8px' }}>
        HIGH
      </span>
    );
  }
  return null;
}

function ControlsTab({ customerId }) {
  const [rachio, setRachio] = useState(null);
  const [busy, setBusy] = useState(null);
  const [runningZone, setRunningZone] = useState(null); // { zoneId, durationSeconds, startedAtMs } | null
  const [editingSchedule, setEditingSchedule] = useState(undefined);
  const [nowMs, setNowMs] = useState(() => Date.now());
  const [zoneMetrics, setZoneMetrics] = useState(null); // { available, rows, anomalies } | null
  const [pausing, setPausing] = useState(false);

  const refresh = useCallback(() => {
    if (!customerId) return;
    fetch(`${BACKEND_URL}/api/rachio/dashboard/${customerId}`).then((r) => r.json()).then(setRachio);
  }, [customerId]);

  useEffect(() => { refresh(); }, [refresh]);

  useEffect(() => {
    if (!customerId) return;
    fetch(`${BACKEND_URL}/api/watersmart/zone-metrics/${customerId}`)
      .then((r) => r.json())
      .then(setZoneMetrics)
      .catch(() => setZoneMetrics({ available: false, rows: [], anomalies: [] }));
  }, [customerId]);

  // Real measured GPM keyed by zone number (falls back to zone name) — see
  // backend/watersmart_zone_metrics.py. Only shown when WaterSmart has
  // enough metered history for this customer; otherwise degrades to the
  // plain runtime display with no invented estimate.
  const zoneMetricsByKey = {};
  (zoneMetrics?.rows || []).forEach((row) => {
    const key = row.zone_number != null ? `n:${row.zone_number}` : `s:${row.zone_name}`;
    zoneMetricsByKey[key] = row;
  });
  const zoneMetricsFor = (z) => {
    if (z.number != null && zoneMetricsByKey[`n:${z.number}`]) return zoneMetricsByKey[`n:${z.number}`];
    return zoneMetricsByKey[`s:${z.name}`] || null;
  };

  useEffect(() => {
    if (!runningZone) return undefined;
    const interval = setInterval(() => setNowMs(Date.now()), 1000);
    return () => clearInterval(interval);
  }, [runningZone]);

  useEffect(() => {
    if (!runningZone) return;
    const elapsedMs = nowMs - runningZone.startedAtMs;
    if (elapsedMs >= runningZone.durationSeconds * 1000) {
      setRunningZone(null);
    }
  }, [nowMs, runningZone]);

  const startZone = async (zone) => {
    const durationSeconds = 300;
    setBusy(zone.id);
    try {
      await fetch(`${BACKEND_URL}/api/rachio/zone/start`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ customer_id: customerId, zone_id: zone.id, duration_seconds: durationSeconds }),
      });
      const startedAtMs = Date.now();
      setNowMs(startedAtMs);
      setRunningZone({ zoneId: zone.id, durationSeconds, startedAtMs });
    } finally {
      setBusy(null);
      setTimeout(refresh, 2000);
    }
  };

  const stopAll = async () => {
    setBusy('stop_all');
    try {
      await fetch(`${BACKEND_URL}/api/rachio/device/stop`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ customer_id: customerId }),
      });
      setRunningZone(null);
    } finally {
      setBusy(null);
      setTimeout(refresh, 2000);
    }
  };

  if (!rachio) return null;

  if (rachio.supported === false || !rachio.controller) {
    return <NotLinkedMessage />;
  }

  const paused = !!(rachio.rain_delay_until && new Date(rachio.rain_delay_until) > new Date());

  const togglePauseAll = async () => {
    setPausing(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/rachio/rain-delay`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ customer_id: customerId, hours: paused ? 0 : 168 }),
      });
      const data = await res.json();
      if (res.ok && data.applied) refresh();
    } finally {
      setPausing(false);
    }
  };

  const zones = rachio.zones || [];

  return (
    <div style={{ marginTop: 24 }}>
      <div
        style={{
          ...CARD_STYLE,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 14,
        }}
      >
        <div>
          <div style={SECTION_LABEL_STYLE}>Zone Controls</div>
          <div style={{ marginTop: 6, fontSize: 14, color: '#cbd5e1' }}>Run a zone now or stop all active watering.</div>
          {paused && (
            <div style={{ fontSize: 13, color: '#fbbf24', marginTop: 6 }}>
              All watering paused until {new Date(rachio.rain_delay_until).toLocaleString()}
            </div>
          )}
        </div>
        <div
          role="button"
          tabIndex={0}
          onClick={busy === 'stop_all' ? undefined : stopAll}
          onKeyDown={(e) => {
            if ((e.key === 'Enter' || e.key === ' ') && busy !== 'stop_all') stopAll();
          }}
          style={{
            borderRadius: 12,
            border: '1px solid rgba(103,232,249,0.2)',
            background: 'rgba(34,211,238,0.1)',
            padding: '11px 18px',
            fontSize: 14,
            fontWeight: 600,
            color: '#cffafe',
            cursor: busy === 'stop_all' ? 'default' : 'pointer',
            opacity: busy === 'stop_all' ? 0.6 : 1,
          }}
        >
          {busy === 'stop_all' ? 'Stopping…' : 'Stop All Zones'}
        </div>
        <button
          onClick={togglePauseAll}
          disabled={pausing}
          style={{
            padding: '10px 18px',
            borderRadius: 12,
            border: '1px solid rgba(255,255,255,0.15)',
            fontSize: 14,
            fontWeight: 600,
            cursor: pausing ? 'default' : 'pointer',
            background: paused ? 'rgba(52,211,153,0.12)' : 'rgba(251,113,133,0.10)',
            color: paused ? '#6ee7b7' : '#fda4af',
          }}
        >
          {pausing ? '…' : paused ? '▶ Resume Watering' : '⏸ Pause All'}
        </button>
      </div>
      <div style={{ marginTop: 16, display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(290px, 1fr))', gap: 16 }}>
        {zones.map((z) => {
          const running = runningZone?.zoneId === z.id;
          const elapsedMs = running ? Math.max(0, nowMs - runningZone.startedAtMs) : 0;
          const remainingSeconds = running
            ? Math.max(0, runningZone.durationSeconds - Math.floor(elapsedMs / 1000))
            : 0;
          const pctElapsed = running
            ? Math.min(1, elapsedMs / (runningZone.durationSeconds * 1000))
            : 0;
          const pctDeg = Math.round(pctElapsed * 360);
          const remainingLabel = `${Math.floor(remainingSeconds / 60)}:${String(remainingSeconds % 60).padStart(2, '0')}`;
          const isBusy = busy === z.id;
          const flowRow = zoneMetricsFor(z);
          const runtimeMin = parseRuntimeMinutes(z.runtime);
          const totalGal = flowRow?.current_gpm != null && runtimeMin != null
            ? Math.round(flowRow.current_gpm * runtimeMin)
            : null;
          return (
            <div
              key={z.id}
              style={{
                borderRadius: 24,
                border: `1px solid ${running ? 'rgba(103,232,249,0.3)' : 'rgba(255,255,255,0.1)'}`,
                background: 'rgba(2,6,23,0.45)',
                padding: 20,
                backdropFilter: 'blur(24px)',
                display: 'flex',
                flexDirection: 'column',
                gap: 12,
                boxSizing: 'border-box',
              }}
            >
              <ZonePhotoSlot zone={z} />
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
                <div>
                  <div style={{ fontSize: 15, fontWeight: 600, color: '#ffffff', display: 'flex', alignItems: 'center', gap: 8 }}>
                    Zone {z.number} · {z.name}
                    {flowRow && <ZoneFlowChip status={flowRow.status} />}
                  </div>
                  <div style={{ fontSize: 13, color: '#94a3b8', marginTop: 3 }}>
                    {z.runtime || '—'} run
                    {flowRow?.current_gpm != null ? ` · ${flowRow.current_gpm} GPM` : ''}
                  </div>
                  {totalGal != null && (
                    <div style={{ fontSize: 12, color: '#67e8f9', marginTop: 2 }}>
                      ≈{totalGal} gal this run
                    </div>
                  )}
                </div>
                {z.status === 'Disabled' && (
                  <div
                    style={{
                      fontSize: 11,
                      fontWeight: 700,
                      color: '#94a3b8',
                      background: 'rgba(255,255,255,0.08)',
                      borderRadius: 999,
                      padding: '4px 10px',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    DISABLED
                  </div>
                )}
              </div>
              <div style={{ fontSize: 13, color: '#64748b' }}>Last run: {z.lastWatered || 'No data'}</div>
              {running ? (
                <div
                  role="button"
                  tabIndex={0}
                  onClick={busy === 'stop_all' ? undefined : stopAll}
                  onKeyDown={(e) => { if ((e.key === 'Enter' || e.key === ' ') && busy !== 'stop_all') stopAll(); }}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 12, borderRadius: 12,
                    border: '1px solid rgba(103,232,249,0.3)', background: 'rgba(34,211,238,0.12)',
                    padding: '10px 14px', cursor: 'pointer',
                  }}
                >
                  <div
                    style={{
                      position: 'relative', width: 44, height: 44, flexShrink: 0, borderRadius: '50%',
                      background: `conic-gradient(#19d3b3 ${pctDeg}deg, rgba(255,255,255,0.12) 0deg)`,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}
                  >
                    <div
                      style={{
                        width: 34, height: 34, borderRadius: '50%', background: 'rgba(6,18,36,0.9)',
                        backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: 10.5, fontWeight: 700, color: '#cffafe',
                      }}
                    >
                      {remainingLabel}
                    </div>
                  </div>
                  <div style={{ flex: 1, fontSize: 14, fontWeight: 700, color: '#cffafe' }}>Running… tap to stop</div>
                </div>
              ) : (
                <div
                  role="button"
                  tabIndex={0}
                  onClick={isBusy ? undefined : () => startZone(z)}
                  onKeyDown={(e) => {
                    if ((e.key === 'Enter' || e.key === ' ') && !isBusy) startZone(z);
                  }}
                  style={{
                    borderRadius: 12,
                    background: 'linear-gradient(90deg, #1094ff, #19d3b3)',
                    padding: 11,
                    fontSize: 14,
                    fontWeight: 700,
                    textAlign: 'center',
                    cursor: isBusy ? 'default' : 'pointer',
                    color: '#ffffff',
                    opacity: isBusy ? 0.7 : 1,
                  }}
                >
                  {isBusy ? 'Starting…' : '▶ Run Zone Now'}
                </div>
              )}
            </div>
          );
        })}
      </div>
      <SchedulesPanel
        customerId={customerId}
        schedules={rachio.schedules || []}
        onRefresh={refresh}
        onAddOrEdit={setEditingSchedule}
      />
      {editingSchedule !== undefined && (
        <ScheduleModal
          customerId={customerId}
          schedule={editingSchedule}
          zones={zones}
          onClose={() => setEditingSchedule(undefined)}
          onSaved={() => { setEditingSchedule(undefined); refresh(); }}
        />
      )}
    </div>
  );
}

function formatDaysLabel(daysOfWeek) {
  const order = ['SUNDAY', 'MONDAY', 'TUESDAY', 'WEDNESDAY', 'THURSDAY', 'FRIDAY', 'SATURDAY'];
  const short = { SUNDAY: 'Sun', MONDAY: 'Mon', TUESDAY: 'Tue', WEDNESDAY: 'Wed', THURSDAY: 'Thu', FRIDAY: 'Fri', SATURDAY: 'Sat' };
  return order.filter((d) => daysOfWeek.includes(d)).map((d) => short[d]).join(', ') || 'No days set';
}

function formatTimeLabel(startHour, startMinute) {
  const period = startHour >= 12 ? 'PM' : 'AM';
  const hour12 = startHour % 12 === 0 ? 12 : startHour % 12;
  return `${hour12}:${String(startMinute).padStart(2, '0')} ${period}`;
}

function SchedulesPanel({ customerId, schedules, onRefresh, onAddOrEdit }) {
  const [busyId, setBusyId] = useState(null);
  const [error, setError] = useState(null);

  const toggleSchedule = async (sch) => {
    setBusyId(sch.id);
    setError(null);
    try {
      const res = await fetch(`${BACKEND_URL}/api/rachio/schedule/toggle`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ customer_id: customerId, schedule_id: sch.id, enabled: !sch.enabled }),
      });
      if (!res.ok) throw new Error('Could not update schedule');
      onRefresh();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusyId(null);
    }
  };

  const deleteSchedule = async (sch) => {
    if (!window.confirm(`Delete "${sch.name}"? This cannot be undone.`)) return;
    setBusyId(sch.id);
    setError(null);
    try {
      const res = await fetch(
        `${BACKEND_URL}/api/rachio/schedule/${sch.id}?customer_id=${encodeURIComponent(customerId)}`,
        { method: 'DELETE' }
      );
      if (!res.ok) throw new Error('Could not delete schedule');
      onRefresh();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div style={{ ...CARD_STYLE, marginTop: 24 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
        <div style={SECTION_LABEL_STYLE}>Watering Schedules</div>
        <div
          role="button"
          tabIndex={0}
          onClick={() => onAddOrEdit(null)}
          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') onAddOrEdit(null); }}
          style={{
            borderRadius: 12,
            background: 'linear-gradient(90deg, #1094ff, #19d3b3)',
            padding: '10px 16px',
            fontSize: 13.5,
            fontWeight: 700,
            color: '#ffffff',
            cursor: 'pointer',
          }}
        >
          + Add Schedule
        </div>
      </div>
      {error && <p style={{ color: 'rgba(255,120,120,0.9)', fontSize: 13, marginTop: 12 }}>{error}</p>}
      <div style={{ marginTop: 16, display: 'flex', flexDirection: 'column', gap: 12 }}>
        {schedules.length === 0 && (
          <div style={{ fontSize: 14, color: '#64748b', padding: '8px 2px' }}>
            No schedules yet — add one to start watering automatically.
          </div>
        )}
        {schedules.map((sch) => (
          <div
            key={sch.id}
            style={{
              borderRadius: 20,
              border: '1px solid rgba(255,255,255,0.1)',
              background: 'rgba(255,255,255,0.03)',
              padding: '16px 18px',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              gap: 14,
              flexWrap: 'wrap',
            }}
          >
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                <div style={{ fontSize: 15, fontWeight: 600, color: '#ffffff' }}>{sch.name}</div>
                <div
                  style={{
                    fontSize: 11,
                    fontWeight: 700,
                    letterSpacing: '0.05em',
                    borderRadius: 999,
                    padding: '3px 10px',
                    background: sch.enabled ? 'rgba(52,211,153,0.15)' : 'rgba(255,255,255,0.08)',
                    color: sch.enabled ? '#a7f3d0' : '#94a3b8',
                  }}
                >
                  {sch.enabled ? 'ACTIVE' : 'PAUSED'}
                </div>
              </div>
              <div style={{ fontSize: 13, color: '#94a3b8', marginTop: 4 }}>
                {formatDaysLabel(sch.days_of_week)} · {formatTimeLabel(sch.start_hour, sch.start_minute)} · {sch.zone_count} zone{sch.zone_count === 1 ? '' : 's'} · ~{Math.round(sch.total_duration_seconds / 60)} min
              </div>
            </div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <div
                role="button"
                tabIndex={0}
                onClick={busyId === sch.id ? undefined : () => toggleSchedule(sch)}
                style={{
                  borderRadius: 10,
                  border: '1px solid rgba(255,255,255,0.12)',
                  padding: '8px 14px',
                  fontSize: 13,
                  fontWeight: 600,
                  color: '#e2e8f0',
                  cursor: busyId === sch.id ? 'default' : 'pointer',
                  opacity: busyId === sch.id ? 0.6 : 1,
                }}
              >
                {sch.enabled ? 'Pause' : 'Enable'}
              </div>
              <div
                role="button"
                tabIndex={0}
                onClick={() => onAddOrEdit(sch)}
                style={{
                  borderRadius: 10,
                  border: '1px solid rgba(103,232,249,0.2)',
                  background: 'rgba(34,211,238,0.1)',
                  padding: '8px 14px',
                  fontSize: 13,
                  fontWeight: 600,
                  color: '#cffafe',
                  cursor: 'pointer',
                }}
              >
                Edit
              </div>
              <div
                role="button"
                tabIndex={0}
                onClick={busyId === sch.id ? undefined : () => deleteSchedule(sch)}
                style={{
                  borderRadius: 10,
                  border: '1px solid rgba(251,113,133,0.25)',
                  background: 'rgba(251,113,133,0.08)',
                  padding: '8px 14px',
                  fontSize: 13,
                  fontWeight: 600,
                  color: '#fda4af',
                  cursor: busyId === sch.id ? 'default' : 'pointer',
                  opacity: busyId === sch.id ? 0.6 : 1,
                }}
              >
                Delete
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

const DAY_OPTIONS = [
  ['SUNDAY', 'Sun'], ['MONDAY', 'Mon'], ['TUESDAY', 'Tue'], ['WEDNESDAY', 'Wed'],
  ['THURSDAY', 'Thu'], ['FRIDAY', 'Fri'], ['SATURDAY', 'Sat'],
];

function ScheduleModal({ customerId, schedule, zones, onClose, onSaved }) {
  const isEdit = !!schedule;
  const [name, setName] = useState(schedule?.name || '');
  const [time, setTime] = useState(
    schedule ? `${String(schedule.start_hour).padStart(2, '0')}:${String(schedule.start_minute).padStart(2, '0')}` : ''
  );
  const [days, setDays] = useState(new Set(schedule?.days_of_week || []));
  const [zoneDurations, setZoneDurations] = useState(() => {
    // Pre-fill from the schedule's real per-zone durations when editing
    // (Task 4's extended summary includes `zones: [{zone_id, duration_seconds}]`).
    // Empty for Add, since there's nothing to pre-fill.
    const initial = {};
    (schedule?.zones || []).forEach((z) => { initial[z.zone_id] = z.duration_seconds; });
    return initial;
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const toggleDay = (day) => {
    setDays((prev) => {
      const next = new Set(prev);
      if (next.has(day)) next.delete(day); else next.add(day);
      return next;
    });
  };

  const toggleZone = (zoneId) => {
    setZoneDurations((prev) => {
      const next = { ...prev };
      if (zoneId in next) delete next[zoneId]; else next[zoneId] = 600; // default 10 min
      return next;
    });
  };

  const save = async () => {
    if (!name.trim()) { setError('Enter a schedule name.'); return; }
    if (days.size === 0) { setError('Select at least one day.'); return; }
    const zoneIds = Object.keys(zoneDurations);
    if (zoneIds.length === 0) { setError('Select at least one zone.'); return; }
    const [hourStr, minuteStr] = (time || '00:00').split(':');

    setSaving(true);
    setError(null);
    try {
      const body = {
        customer_id: customerId,
        name: name.trim(),
        start_hour: parseInt(hourStr, 10),
        start_minute: parseInt(minuteStr, 10),
        days_of_week: Array.from(days),
        zones: zoneIds.map((zoneId) => ({ zone_id: zoneId, duration_seconds: zoneDurations[zoneId] })),
      };
      const url = isEdit
        ? `${BACKEND_URL}/api/rachio/schedule/${schedule.id}`
        : `${BACKEND_URL}/api/rachio/schedule/create`;
      const res = await fetch(url, {
        method: isEdit ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!res.ok) throw new Error('Could not save schedule');
      onSaved();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      style={{
        position: 'fixed', inset: 0, background: 'rgba(2,10,20,0.7)', backdropFilter: 'blur(6px)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 50, padding: 20,
      }}
      onClick={onClose}
    >
      <div
        style={{
          background: 'rgba(6,18,36,0.97)', border: '1px solid rgba(255,255,255,0.12)', borderRadius: 24,
          padding: 28, maxWidth: 480, width: '100%', boxSizing: 'border-box',
          boxShadow: '0 30px 70px rgba(0,0,0,0.6)', maxHeight: '86vh', overflowY: 'auto',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div style={{ fontSize: 20, fontWeight: 600, color: '#ffffff' }}>{isEdit ? 'Edit Schedule' : 'Add Schedule'}</div>

        <div style={{ marginTop: 18, color: 'rgba(255,255,255,0.6)', fontSize: 11, fontWeight: 700, letterSpacing: '2px', textTransform: 'uppercase', marginBottom: 8 }}>
          Schedule Name
        </div>
        <div style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 12, padding: 4 }}>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            style={{ width: '100%', padding: '12px 13px', background: 'transparent', border: 'none', borderRadius: 9, color: '#ffffff', fontSize: 14.5, outline: 'none', boxSizing: 'border-box' }}
          />
        </div>

        <div style={{ marginTop: 16, color: 'rgba(255,255,255,0.6)', fontSize: 11, fontWeight: 700, letterSpacing: '2px', textTransform: 'uppercase', marginBottom: 8 }}>
          Start Time
        </div>
        <div style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 12, padding: 4 }}>
          <input
            type="time"
            value={time}
            onChange={(e) => setTime(e.target.value)}
            style={{ width: '100%', padding: '12px 13px', background: 'transparent', border: 'none', borderRadius: 9, color: '#ffffff', fontSize: 14.5, outline: 'none', boxSizing: 'border-box' }}
          />
        </div>

        <div style={{ marginTop: 16, color: 'rgba(255,255,255,0.6)', fontSize: 11, fontWeight: 700, letterSpacing: '2px', textTransform: 'uppercase', marginBottom: 8 }}>
          Days
        </div>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {DAY_OPTIONS.map(([value, label]) => {
            const active = days.has(value);
            return (
              <div
                key={value}
                role="button"
                tabIndex={0}
                onClick={() => toggleDay(value)}
                style={{
                  borderRadius: 999,
                  border: `1px solid ${active ? 'rgba(103,232,249,0.4)' : 'rgba(255,255,255,0.1)'}`,
                  background: active ? 'rgba(34,211,238,0.15)' : 'rgba(255,255,255,0.03)',
                  color: active ? '#cffafe' : '#94a3b8',
                  padding: '8px 13px',
                  fontSize: 13,
                  fontWeight: 600,
                  cursor: 'pointer',
                }}
              >
                {label}
              </div>
            );
          })}
        </div>

        <div style={{ marginTop: 16, color: 'rgba(255,255,255,0.6)', fontSize: 11, fontWeight: 700, letterSpacing: '2px', textTransform: 'uppercase', marginBottom: 8 }}>
          Zones to Run
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {zones.map((z) => {
            const checked = z.id in zoneDurations;
            return (
              <div
                key={z.id}
                role="button"
                tabIndex={0}
                onClick={() => toggleZone(z.id)}
                style={{
                  borderRadius: 12,
                  border: `1px solid ${checked ? 'rgba(103,232,249,0.4)' : 'rgba(255,255,255,0.1)'}`,
                  background: checked ? 'rgba(34,211,238,0.1)' : 'rgba(255,255,255,0.03)',
                  padding: '10px 14px',
                  fontSize: 14,
                  fontWeight: 500,
                  color: checked ? '#cffafe' : '#e2e8f0',
                  cursor: 'pointer',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                }}
              >
                <span>Zone {z.number} · {z.name}</span>
                <span style={{ fontSize: 18 }}>{checked ? '✓' : ''}</span>
              </div>
            );
          })}
        </div>

        {error && <p style={{ color: 'rgba(255,120,120,0.9)', fontSize: 13, marginTop: 12 }}>{error}</p>}

        <div style={{ display: 'flex', gap: 10, marginTop: 24 }}>
          <div
            role="button"
            tabIndex={0}
            onClick={onClose}
            style={{ flex: 1, border: '1px solid rgba(255,255,255,0.15)', borderRadius: 12, padding: 13, fontSize: 14.5, fontWeight: 600, textAlign: 'center', cursor: 'pointer', color: '#cbd5e1' }}
          >
            Cancel
          </div>
          <div
            role="button"
            tabIndex={0}
            onClick={saving ? undefined : save}
            style={{
              flex: 1.4, background: 'linear-gradient(90deg, #1094ff, #19d3b3)', color: '#ffffff', borderRadius: 12,
              padding: 13, fontSize: 14.5, fontWeight: 700, textAlign: 'center', cursor: saving ? 'default' : 'pointer',
              opacity: saving ? 0.7 : 1,
            }}
          >
            {saving ? 'Saving…' : isEdit ? 'Save Changes' : 'Add Schedule'}
          </div>
        </div>
      </div>
    </div>
  );
}

function formatGal(n) {
  return `${Math.round(n).toLocaleString()} gal`;
}

function formatUsd(n) {
  return `$${n.toFixed(2)}`;
}

function formatDayLabel(dateStr) {
  const d = new Date(`${dateStr}T00:00:00`);
  if (Number.isNaN(d.getTime())) return dateStr;
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function UsageTab({ customerId }) {
  const [billing, setBilling] = useState(null);
  const [history, setHistory] = useState(null);
  const [savings, setSavings] = useState(null);
  // Same zone-metrics endpoint the Controls/Overview/Alerts/History tabs each
  // fetch independently (no shared cache — matches this file's convention).
  // Used only to compute the "GPM cut" line under each Water Saved card.
  const [zoneMetrics, setZoneMetrics] = useState(null);
  const [saveSuggestion, setSaveSuggestion] = useState(null);
  const [savingApplied, setSavingApplied] = useState(false);
  const [applying, setApplying] = useState(false);
  const [applyError, setApplyError] = useState(null);

  useEffect(() => {
    if (!customerId) return;
    fetch(`${BACKEND_URL}/api/watersmart/billing/${customerId}`).then((r) => r.json()).then(setBilling);
    fetch(`${BACKEND_URL}/api/watersmart/usage-history/${customerId}?days=14`).then((r) => r.json()).then(setHistory);
    fetch(`${BACKEND_URL}/api/watersmart/savings/${customerId}`).then((r) => r.json()).then(setSavings);
    fetch(`${BACKEND_URL}/api/watersmart/zone-metrics/${customerId}`).then((r) => r.json()).then(setZoneMetrics);
    fetch(`${BACKEND_URL}/api/watersmart/save-suggestion/${customerId}`).then((r) => r.json()).then(setSaveSuggestion);
  }, [customerId]);

  // Server recomputes+enforces mode/est_gal/est_usd/pause_hours itself (see
  // watersmart_apply_saving's B2 fix pack note in backend/server.py) — the
  // fields sent here are accepted for wire compat but ignored server-side,
  // so a stale/tampered client can't inflate recorded savings.
  async function applySaving() {
    setApplying(true);
    setApplyError(null);
    try {
      const res = await fetch(`${BACKEND_URL}/api/watersmart/apply-saving`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ customer_id: customerId, ...saveSuggestion }),
      });
      let data;
      try {
        data = await res.json();
      } catch {
        setApplyError("Couldn't apply — try again");
        return;
      }
      if (res.ok && data.applied) {
        setSavingApplied(true);
      } else {
        setApplyError(data.error || "Couldn't apply — try again");
      }
    } finally {
      setApplying(false);
    }
  }

  if (billing === null || history === null) return null;

  if (!billing.available) {
    return (
      <div style={{ ...CARD_STYLE, marginTop: 24, textAlign: 'center' }}>
        <div style={{ fontSize: 15, fontWeight: 600, color: '#ffffff' }}>Usage data not synced yet</div>
        <div style={{ marginTop: 8, fontSize: 14, color: '#94a3b8', lineHeight: 1.5 }}>
          Your first WaterSmart sync hasn&rsquo;t completed yet. Check back after the next daily sync.
        </div>
      </div>
    );
  }

  const usagePct = Math.min(100, (billing.usage_to_date / billing.tier_breakpoint_gal) * 60);
  const projPct = Math.min(100, (billing.projected_gal / billing.tier_breakpoint_gal) * 60);

  const days = history.days || [];
  // Scale factor computed dynamically from the real fetched data (not a hardcoded
  // mockup constant) so bars stay legible for light users and never overflow the
  // fixed-height chart container for heavy irrigators. 150 matches the 180px chart
  // container's target max bar height, leaving headroom.
  const maxDailyTotal = Math.max(
    1, // floor to avoid divide-by-zero when there's no data yet
    ...days.map((d) => (d.irrigation || 0) + (d.household || 0) + (d.leak || 0))
  );
  const scaleD = 150 / maxDailyTotal;
  const dailyBars = days.map((d) => ({
    date: d.date,
    label: formatDayLabel(d.date),
    hhPx: Math.max(0, d.household) * scaleD,
    irrPx: Math.max(0, d.irrigation) * scaleD,
    leakPx: Math.max(0, d.leak) * scaleD,
    title: `${formatDayLabel(d.date)} — irrigation ${formatGal(d.irrigation)}, household ${formatGal(d.household)}, unexplained flow ${formatGal(d.leak)}`,
  }));
  // Average |delta_pct| across zones that improved (negative delta = using
  // less than expected) since the last GPM check — a rough proxy for how
  // much of "Water Saved" traces back to a real flow-rate fix vs. just fewer
  // runs. Zero when zone-metrics isn't available yet or no zone improved.
  const gpmCut = zoneMetrics && zoneMetrics.rows
    ? zoneMetrics.rows.filter((r) => r.delta_pct != null && r.delta_pct < 0)
        .reduce((acc, r, _, arr) => acc + Math.abs(r.delta_pct) / arr.length, 0)
    : 0;

  return (
    <div style={{ marginTop: 24, display: 'flex', flexDirection: 'column', gap: 24 }}>
      <div style={CARD_STYLE}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', flexWrap: 'wrap', gap: 8 }}>
          <div>
            <div style={SECTION_LABEL_STYLE}>Billing Tier Status</div>
            <div style={{ marginTop: 8, display: 'flex', alignItems: 'baseline', gap: 10, flexWrap: 'wrap' }}>
              <div style={{ fontSize: 32, fontWeight: 600, color: '#ffffff' }}>{formatGal(billing.usage_to_date)}</div>
              <div style={{ fontSize: 14, color: '#cbd5e1' }}>
                this cycle · <strong style={{ color: '#ffffff' }}>{formatUsd(billing.cost_to_date)}</strong> so far
              </div>
            </div>
          </div>
          <div style={{ fontSize: 13, color: '#94a3b8' }}>Cycle started {billing.cycle_start}</div>
        </div>
        <div style={{ marginTop: 20, position: 'relative', height: 24, borderRadius: 12, background: 'rgba(255,255,255,0.08)', overflow: 'hidden' }}>
          <div style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: `${projPct}%`, background: 'rgba(56,189,248,0.25)', borderRadius: 12 }} />
          <div style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: `${usagePct}%`, background: 'linear-gradient(90deg, #1094ff, #19d3b3)', borderRadius: 12 }} />
          <div style={{ position: 'absolute', left: '60%', top: 0, bottom: 0, width: 2, background: '#fbbf24' }} />
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 8, fontSize: 12.5, color: '#94a3b8' }}>
          <div>Tier 1</div>
          <div style={{ color: '#fde68a', fontWeight: 600 }}>{formatGal(billing.tier_breakpoint_gal)} → Tier 2</div>
        </div>
        <div style={{ marginTop: 12, fontSize: 14, color: '#cbd5e1' }}>
          Projected to finish cycle around <strong style={{ color: '#ffffff' }}>{formatGal(billing.projected_gal)}</strong>
          {' '}(<strong style={{ color: '#ffffff' }}>{formatUsd(billing.projected_cost)}</strong> projected).
        </div>
      </div>

      {saveSuggestion && saveSuggestion.available && !savingApplied && (
        <div style={{ ...CARD_STYLE, border: '1px solid rgba(110,231,183,0.25)', background: 'rgba(52,211,153,0.06)' }}>
          <div style={SECTION_LABEL_STYLE}>Conservation Suggestion</div>
          <div style={{ marginTop: 10, fontSize: 16, fontWeight: 600, color: '#ffffff' }}>
            {saveSuggestion.mode === 'skip'
              ? `Skip Next Run — Save ~$${saveSuggestion.est_usd.toFixed(2)}`
              : `Pause Sprinklers 4 Days — Save ~$${saveSuggestion.est_usd.toFixed(2)}`}
          </div>
          <div style={{ marginTop: 8, fontSize: 14, color: '#cbd5e1' }}>
            That&rsquo;s about {Math.round(saveSuggestion.est_gal).toLocaleString()} gallons at your current tier rate.
          </div>
          <button
            onClick={applySaving}
            disabled={applying}
            style={{
              marginTop: 14, padding: '10px 22px', borderRadius: 12, border: 'none', cursor: 'pointer',
              fontSize: 14, fontWeight: 600, color: '#ffffff',
              background: 'linear-gradient(90deg, #1094ff, #19d3b3)',
              boxShadow: '0 10px 25px rgba(0,150,150,0.25)',
            }}
          >
            {applying ? 'Applying…' : 'Apply'}
          </button>
          {applyError && (
            <div style={{ marginTop: 10, fontSize: 13, color: '#fda4af' }}>{applyError}</div>
          )}
        </div>
      )}
      {savingApplied && (
        <div style={{ ...CARD_STYLE, border: '1px solid rgba(110,231,183,0.25)', background: 'rgba(52,211,153,0.08)' }}>
          <div style={{ fontSize: 15, fontWeight: 600, color: '#6ee7b7' }}>✓ Watering paused</div>
          <div style={{ marginTop: 6, fontSize: 14, color: '#cbd5e1' }}>
            Established Texas lawns comfortably handle a few days without irrigation — your soil moisture will carry it. Savings will show up under Water Saved below.
          </div>
        </div>
      )}

      <div style={CARD_STYLE}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', flexWrap: 'wrap', gap: 8 }}>
          <div style={SECTION_LABEL_STYLE}>Daily Usage — Last {days.length} Days</div>
          <div style={{ display: 'flex', gap: 16, fontSize: 12.5, color: '#94a3b8', flexWrap: 'wrap' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <div style={{ width: 10, height: 10, borderRadius: 3, background: '#38bdf8' }} />
              Irrigation
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <div style={{ width: 10, height: 10, borderRadius: 3, background: 'rgba(148,163,184,0.45)' }} />
              Household
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <div style={{ width: 10, height: 10, borderRadius: 3, background: '#fb7185' }} />
              Unexplained flow
            </div>
          </div>
        </div>
        {dailyBars.length === 0 ? (
          <div style={{ marginTop: 24, fontSize: 14, color: '#94a3b8' }}>No daily usage history yet.</div>
        ) : (
          <>
            <div style={{ display: 'flex', alignItems: 'flex-end', gap: 6, height: 180, marginTop: 24 }}>
              {dailyBars.map((bar) => (
                <div
                  key={bar.date}
                  title={bar.title}
                  style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'flex-end', height: '100%', minWidth: 0 }}
                >
                  <div style={{ width: '70%', maxWidth: 34, height: bar.leakPx, background: '#fb7185', borderRadius: '3px 3px 0 0' }} />
                  <div style={{ width: '70%', maxWidth: 34, height: bar.irrPx, background: '#38bdf8' }} />
                  <div style={{ width: '70%', maxWidth: 34, height: bar.hhPx, background: 'rgba(148,163,184,0.45)', borderRadius: '0 0 3px 3px' }} />
                </div>
              ))}
            </div>
            <div style={{ display: 'flex', gap: 6, marginTop: 8 }}>
              {dailyBars.map((bar) => (
                <div key={bar.date} style={{ flex: 1, textAlign: 'center', fontSize: 10.5, color: '#94a3b8', minWidth: 0, overflow: 'hidden' }}>
                  {bar.label}
                </div>
              ))}
            </div>
          </>
        )}
      </div>

      {savings && (
        <div>
          <div style={SECTION_LABEL_STYLE}>Water Saved</div>
          <div style={{ marginTop: 12, display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 16 }}>
            {[['This Month', savings.month], ['This Year', savings.year], ['All-Time Total', savings.all_time]].map(([label, s]) => (
              <div key={label} style={{ ...CARD_STYLE, border: '1px solid rgba(110,231,183,0.15)', background: 'rgba(52,211,153,0.06)' }}>
                <div style={{ fontSize: 14, color: '#cbd5e1' }}>{label}</div>
                <div style={{ marginTop: 10, fontSize: 28, fontWeight: 600, color: '#ffffff' }}>
                  {Math.round(s.gal).toLocaleString()} gal
                </div>
                <div style={{ marginTop: 6, fontSize: 14, color: '#6ee7b7' }}>${s.usd.toFixed(2)} saved</div>
                {gpmCut > 0 && <div style={{ marginTop: 4, fontSize: 12.5, color: '#94a3b8' }}>avg {gpmCut.toFixed(0)}% GPM cut across improved zones</div>}
              </div>
            ))}
          </div>
          <div style={{ marginTop: 8, fontSize: 12.5, color: '#94a3b8' }}>
            Counted from conservation actions applied in this portal. Powered by Boatman Systems™
          </div>
        </div>
      )}
    </div>
  );
}

function formatRunDate(isoStr) {
  if (!isoStr) return '—';
  const d = new Date(isoStr);
  if (Number.isNaN(d.getTime())) return isoStr;
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function formatRunTime(isoStr) {
  if (!isoStr) return '—';
  const d = new Date(isoStr);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
}

function formatDuration(totalMinutes) {
  const mins = Math.round(totalMinutes || 0);
  if (mins < 60) return `${mins} min`;
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return m === 0 ? `${h} hr` : `${h} hr ${m} min`;
}

const HISTORY_MODES = [
  { key: 'runs', label: 'Per Run' },
  { key: 'week', label: 'Weekly' },
  { key: 'month', label: 'Monthly' },
  { key: 'gpm', label: 'Zone GPM' },
];

// Bar chart of the real `daily` array returned by GET /api/rachio/usage/{customer_id}.
// Note: the mockup (water-conservation-mockup.html lines 386-424) shows a 4-bar
// weekly rollup spanning ~a month and a 6-bar monthly rollup spanning ~6 months.
// The real backend has no such multi-period aggregation — `period=week` returns the
// last 7 days of daily totals and `period=month` returns the current calendar
// month's daily totals (backend/server.py rachio_usage, ~L1936-2027). Per the
// project's "no fabricated integrations" rule (already applied in Tasks 8-9),
// this renders the real daily data instead of synthesizing rollups that don't
// exist. Bar heights are scaled dynamically from the fetched data's own max
// value — never a mockup-derived constant.
function DailyUsageChart({ usage }) {
  if (!usage) return null;
  const daily = usage.daily || [];

  if (daily.length === 0) {
    return (
      <div style={CARD_STYLE}>
        <div style={SECTION_LABEL_STYLE}>{usage.period_label || 'Usage'}</div>
        <div style={{ marginTop: 20, fontSize: 14, color: '#94a3b8' }}>No watering runs recorded for this period.</div>
      </div>
    );
  }

  const TARGET_MAX_PX = 160;
  const maxGallons = Math.max(1, ...daily.map((d) => d.gallons || 0));
  const scale = TARGET_MAX_PX / maxGallons;
  const bars = daily.map((d) => ({
    date: d.date,
    label: formatDayLabel(d.date),
    px: Math.max(2, (d.gallons || 0) * scale),
    gallons: d.gallons || 0,
  }));

  return (
    <div style={CARD_STYLE}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', flexWrap: 'wrap', gap: 8 }}>
        <div>
          <div style={SECTION_LABEL_STYLE}>{usage.period_label || 'Usage'}</div>
          <div style={{ marginTop: 8, fontSize: 24, fontWeight: 600, color: '#ffffff' }}>{formatGal(usage.total_gallons || 0)}</div>
        </div>
        <div style={{ fontSize: 13, color: '#94a3b8' }}>Daily irrigation totals from your Rachio controller</div>
      </div>
      <div style={{ display: 'flex', alignItems: 'flex-end', gap: 6, height: TARGET_MAX_PX, marginTop: 24 }}>
        {bars.map((bar) => (
          <div
            key={bar.date}
            title={`${bar.label} — ${formatGal(bar.gallons)}`}
            style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'flex-end', height: '100%', minWidth: 0 }}
          >
            <div style={{ width: '70%', maxWidth: 34, height: bar.px, background: '#38bdf8', borderRadius: '3px 3px 0 0' }} />
          </div>
        ))}
      </div>
      <div style={{ display: 'flex', gap: 6, marginTop: 8 }}>
        {bars.map((bar) => (
          <div key={bar.date} style={{ flex: 1, textAlign: 'center', fontSize: 10.5, color: '#94a3b8', minWidth: 0, overflow: 'hidden' }}>
            {bar.label}
          </div>
        ))}
      </div>
    </div>
  );
}

function HistoryTab({ customerId }) {
  const [mode, setMode] = useState('runs');
  const [runs, setRuns] = useState(null);
  const [weekly, setWeekly] = useState(null);
  const [monthly, setMonthly] = useState(null);
  const [gpm, setGpm] = useState(null);

  useEffect(() => {
    if (!customerId) return;
    if (mode === 'runs' && runs === null) {
      fetch(`${BACKEND_URL}/api/rachio/history/${customerId}`)
        .then((r) => r.json())
        .then((d) => setRuns(d.runs || []));
    } else if (mode === 'week' && weekly === null) {
      fetch(`${BACKEND_URL}/api/rachio/usage/${customerId}?period=week`)
        .then((r) => r.json())
        .then(setWeekly);
    } else if (mode === 'month' && monthly === null) {
      fetch(`${BACKEND_URL}/api/rachio/usage/${customerId}?period=month`)
        .then((r) => r.json())
        .then(setMonthly);
    } else if (mode === 'gpm' && gpm === null) {
      fetch(`${BACKEND_URL}/api/watersmart/zone-metrics/${customerId}`)
        .then((r) => r.json())
        .then(setGpm);
    }
  }, [mode, customerId, runs, weekly, monthly, gpm]);

  return (
    <div style={{ marginTop: 24, display: 'flex', flexDirection: 'column', gap: 20 }}>
      <div style={{ display: 'flex', borderRadius: 12, border: '1px solid rgba(255,255,255,0.1)', overflow: 'hidden', width: 'fit-content' }}>
        {HISTORY_MODES.map((ht) => {
          const active = mode === ht.key;
          return (
            <div
              key={ht.key}
              role="button"
              tabIndex={0}
              onClick={() => setMode(ht.key)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') setMode(ht.key);
              }}
              style={{
                padding: '10px 20px',
                fontSize: 14,
                fontWeight: 600,
                cursor: 'pointer',
                background: active ? 'linear-gradient(90deg, #1094ff, #19d3b3)' : 'transparent',
                color: active ? '#ffffff' : '#94a3b8',
              }}
            >
              {ht.label}
            </div>
          );
        })}
      </div>

      {mode === 'runs' && (
        runs === null ? null : runs.length === 0 ? (
          <div style={{ ...CARD_STYLE, textAlign: 'center' }}>
            <div style={{ fontSize: 15, fontWeight: 600, color: '#ffffff' }}>No watering runs recorded yet</div>
            <div style={{ marginTop: 8, fontSize: 14, color: '#94a3b8', lineHeight: 1.5 }}>
              Runs will show up here after your Rachio controller waters a zone.
            </div>
          </div>
        ) : (
          <div style={{ borderRadius: 28, border: '1px solid rgba(255,255,255,0.1)', background: 'rgba(2,6,23,0.45)', backdropFilter: 'blur(24px)', overflow: 'hidden' }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1.3fr 1fr 1fr 1fr 1fr', gap: 8, padding: '16px 24px', background: 'rgba(255,255,255,0.05)', fontSize: 13, fontWeight: 500, color: '#cbd5e1' }}>
              <div>Run Date</div>
              <div>Start</div>
              <div>Duration</div>
              <div>Zones</div>
              <div>Gallons</div>
            </div>
            {runs.map((r, idx) => (
              <div
                key={`${r.started_at}-${idx}`}
                style={{
                  display: 'grid',
                  gridTemplateColumns: '1.3fr 1fr 1fr 1fr 1fr',
                  gap: 8,
                  padding: '16px 24px',
                  borderTop: '1px solid rgba(255,255,255,0.08)',
                  fontSize: 14,
                  background: idx % 2 === 0 ? 'transparent' : 'rgba(15,23,42,0.2)',
                  alignItems: 'center',
                }}
              >
                <div style={{ fontWeight: 600, color: '#ffffff' }}>{formatRunDate(r.started_at)}</div>
                <div style={{ color: '#94a3b8' }}>{formatRunTime(r.started_at)}</div>
                <div style={{ color: '#94a3b8' }}>{formatDuration(r.total_minutes)}</div>
                <div style={{ color: '#94a3b8' }}>{(r.zones || []).length}</div>
                <div style={{ fontWeight: 600, color: '#e2e8f0' }}>
                  {r.total_gallons != null ? formatGal(r.total_gallons) : '—'}
                </div>
              </div>
            ))}
          </div>
        )
      )}

      {mode === 'week' && <DailyUsageChart usage={weekly} />}
      {mode === 'month' && <DailyUsageChart usage={monthly} />}

      {mode === 'gpm' && gpm && (
        <div style={CARD_STYLE}>
          <div style={{ fontSize: 13.5, color: '#94a3b8', marginBottom: 16 }}>
            Flow rate (GPM) is derived from your water meter&rsquo;s readings during each zone&rsquo;s runs — no test runs needed. A rising GPM usually means a broken head; near-zero means a valve isn&rsquo;t opening.
          </div>
          {!gpm.available || gpm.rows.length === 0 ? (
            <div style={{ fontSize: 14, color: '#94a3b8' }}>Not enough synced run data yet — check back after a few watering days.</div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1.6fr 1fr 1fr 1fr 1fr', fontSize: 13, color: '#cbd5e1', background: 'rgba(255,255,255,0.05)', borderRadius: 12 }}>
                {['Zone', 'Expected GPM', 'Current GPM', 'Δ%', 'Status'].map((h) => (
                  <div key={h} style={{ padding: '12px 16px', fontWeight: 600 }}>{h}</div>
                ))}
              </div>
              {gpm.rows.map((r) => {
                const chip = r.status === 'no_flow'
                  ? { label: 'NO FLOW', color: '#fda4af', bg: 'rgba(251,113,133,0.15)' }
                  : r.status === 'overage'
                    ? { label: 'HIGH', color: '#fde68a', bg: 'rgba(251,191,36,0.15)' }
                    : r.status === 'pending_data'
                      ? { label: 'PENDING DATA', color: '#94a3b8', bg: 'rgba(148,163,184,0.15)' }
                      : { label: 'OK', color: '#6ee7b7', bg: 'rgba(52,211,153,0.12)' };
                return (
                  <div key={r.zone_name} style={{ display: 'grid', gridTemplateColumns: '1.6fr 1fr 1fr 1fr 1fr', fontSize: 14, color: '#ffffff', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
                    <div style={{ padding: '12px 16px' }}>{r.zone_name}</div>
                    <div style={{ padding: '12px 16px' }}>{r.expected_gpm ?? '—'}</div>
                    <div style={{ padding: '12px 16px' }}>{r.current_gpm}</div>
                    <div style={{ padding: '12px 16px', color: r.delta_pct > 15 ? '#fde68a' : '#cbd5e1' }}>
                      {r.delta_pct == null ? '—' : `${r.delta_pct > 0 ? '+' : ''}${r.delta_pct}%`}
                    </div>
                    <div style={{ padding: '12px 16px' }}>
                      <span style={{ borderRadius: 999, padding: '3px 10px', fontSize: 11.5, fontWeight: 700, background: chip.bg, color: chip.color }}>{chip.label}</span>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function severityStyle(severity) {
  if (severity === 'major') {
    return { border: 'rgba(251,113,133,0.25)', chipBg: 'rgba(251,113,133,0.15)', chipColor: '#fda4af', label: 'MAJOR LEAK' };
  }
  return { border: 'rgba(251,191,36,0.25)', chipBg: 'rgba(251,191,36,0.15)', chipColor: '#fde68a', label: 'POSSIBLE LEAK' };
}

// Trip-date picker options. The mockup (lines 555-561) offers 3 fixed choices with a
// label + time window; here the 3 dates are computed relative to today (next 3 days)
// rather than hardcoded, and the time windows reuse the same options already offered
// by ScheduleServiceForm in App.js (8AM-11AM / 11AM-2PM / 2PM-5PM) so the value sent
// to /api/schedule-appointment matches what that endpoint already expects elsewhere.
// A 4th flexible option is appended on the earliest available date (2026-07-20, per
// John's order that every customer-facing schedule surface offer a flexible choice).
// It sends "Any Time" — the CUSTOMER-facing canonical value (Quotation.custom_scheduled_time
// Select option / ERP fixture). "All Day" is a separate, admin/staff-only option for
// genuine full-day jobs booked from the desk UI and must never be offered here.
// "Any Time" is the exact canonical value, not a free-text label, so it round-trips
// correctly with no normalization needed.
//
// Each option carries TWO date representations:
//   - dateLabel: human-readable ("Tue, Jul 15") shown to the customer in the modal.
//   - isoDate: "YYYY-MM-DD" sent as `preferred_date` to the backend, which writes it
//     verbatim into Quotation.custom_scheduled_date (a Frappe Date fieldtype). This
//     mirrors ScheduleServiceForm in App.js, which sources preferred_date from a native
//     <input type="date"> that always yields ISO strings.
//   isoDate is built from local Y/M/D components (not toISOString(), which converts to
//   UTC and can roll the date backward by one day for any local time after 6-8PM
//   depending on timezone offset) so it always matches the calendar date shown in dateLabel.
function buildTripOptions() {
  const windows = ['8AM-11AM', '11AM-2PM', '2PM-5PM'];
  const opts = [];
  for (let i = 1; i <= 3; i += 1) {
    const d = new Date();
    d.setDate(d.getDate() + i);
    const label = d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
    const isoDate = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    opts.push({ dateLabel: label, isoDate, window: windows[i - 1] });
  }
  if (opts.length > 0) {
    opts.push({ dateLabel: `${opts[0].dateLabel} (any time)`, isoDate: opts[0].isoDate, window: 'Any Time' });
  }
  return opts;
}

function AlertsTab({ customerId }) {
  const [leak, setLeak] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [tripConfirmed, setTripConfirmed] = useState(false);
  const [confirmedLabel, setConfirmedLabel] = useState('');
  const [scheduling, setScheduling] = useState(false);
  const [error, setError] = useState(null);
  // Same zone-metrics endpoint the other tabs fetch independently (no shared
  // cache). Used only for the "Likely zone" leak-attribution line below.
  const [zoneMetrics, setZoneMetrics] = useState(null);

  useEffect(() => {
    if (!customerId) return;
    fetch(`${BACKEND_URL}/api/watersmart/leak-status/${customerId}`).then((r) => r.json()).then(setLeak);
    fetch(`${BACKEND_URL}/api/watersmart/zone-metrics/${customerId}`).then((r) => r.json()).then(setZoneMetrics);
  }, [customerId]);

  const confirmTrip = async (dateLabel, isoDate, preferredTime) => {
    if (scheduling) return;
    setScheduling(true);
    setError(null);
    try {
      const customer = JSON.parse(localStorage.getItem('customer') || '{}');
      const res = await fetch(`${BACKEND_URL}/api/schedule-appointment`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: customer.name || customer.customer_name || undefined,
          email: customer.email || '',
          service: 'Leak Repair',
          preferred_date: isoDate,
          preferred_time: preferredTime,
          notes: `Leak repair requested via Water Conservation Portal — ${leak.severity} leak, ~${leak.rate_gal_hr} gal/hr overnight.`,
        }),
      });
      if (!res.ok) {
        setError('Could not schedule the trip. Please try again or call us directly.');
        return;
      }
      setModalOpen(false);
      setTripConfirmed(true);
      setConfirmedLabel(dateLabel);
    } catch {
      setError('Could not reach the server. Please try again.');
    } finally {
      setScheduling(false);
    }
  };

  if (!leak) return null;

  if (!leak.detected) {
    return (
      <div style={{ marginTop: 24, display: 'flex', flexDirection: 'column', gap: 16 }}>
        <div style={{ ...CARD_STYLE, textAlign: 'center' }}>
          <div style={{ fontSize: 15, fontWeight: 600, color: '#ffffff' }}>All Clear — No Leaks Detected</div>
          <div style={{ marginTop: 8, fontSize: 14, color: '#94a3b8', lineHeight: 1.5 }}>
            We didn&rsquo;t find any unusual overnight flow in your recent WaterSmart readings.
            We&rsquo;ll alert you here if that changes.
          </div>
        </div>
        <ReferralBanner customerId={customerId} />
      </div>
    );
  }

  const sevStyle = severityStyle(leak.severity);
  const tripOpts = buildTripOptions();

  return (
    <div style={{ marginTop: 24, display: 'flex', flexDirection: 'column', gap: 20 }}>
      <div style={{ ...CARD_STYLE, border: `1px solid ${sevStyle.border}` }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 12 }}>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
              <div style={{ fontSize: 18, fontWeight: 600, color: '#ffffff' }}>Possible Leak Detected</div>
              <div
                style={{
                  fontSize: 11,
                  fontWeight: 700,
                  letterSpacing: '0.08em',
                  borderRadius: 999,
                  padding: '4px 12px',
                  background: sevStyle.chipBg,
                  color: sevStyle.chipColor,
                }}
              >
                {sevStyle.label}
              </div>
            </div>
            <div style={{ fontSize: 14, color: '#cbd5e1', marginTop: 8, lineHeight: 1.55, maxWidth: 640 }}>
              Your meter shows steady flow of about{' '}
              <strong style={{ color: '#ffffff' }}>{leak.rate_gal_hr} gal/hr</strong>, consistent with a leak
              rather than normal household use. That pattern usually points to a stuck valve or a
              leaking line.
            </div>
          </div>
          {tripConfirmed ? (
            <div
              style={{
                borderRadius: 12,
                border: '1px solid rgba(110,231,183,0.2)',
                background: 'rgba(52,211,153,0.1)',
                padding: '12px 20px',
                fontSize: 14,
                fontWeight: 600,
                color: '#a7f3d0',
                whiteSpace: 'nowrap',
              }}
            >
              ✓ Trip Scheduled — {confirmedLabel}
            </div>
          ) : (
            <div
              role="button"
              tabIndex={0}
              onClick={() => setModalOpen(true)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') setModalOpen(true);
              }}
              style={{
                borderRadius: 12,
                background: 'linear-gradient(90deg, #1094ff, #19d3b3)',
                padding: '12px 20px',
                fontSize: 14,
                fontWeight: 700,
                color: '#ffffff',
                cursor: 'pointer',
                whiteSpace: 'nowrap',
              }}
            >
              Schedule Service Trip
            </div>
          )}
        </div>

        {/* Note: the mockup's hour-by-hour "Overnight flow (gal/hr)" bar chart (lines
            455-470) is intentionally omitted here. GET /api/watersmart/leak-status/{id}
            only returns aggregate fields (detected, severity, rate_gal_hr, wasted_gal_7d,
            cost_per_month) — no per-hour breakdown is exposed by any API response, so
            there is no real data to back that chart. Per the project's "no fabricated
            data" rule (already applied the same way in Tasks 8-10), we render only the
            stat tiles the endpoint actually supports. */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))', gap: 16, marginTop: 20 }}>
          <div style={{ borderRadius: 20, border: '1px solid rgba(251,113,133,0.15)', background: 'rgba(251,113,133,0.08)', padding: '14px 18px' }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: '#fda4af', textTransform: 'uppercase', letterSpacing: '0.1em' }}>
              Water Wasted
            </div>
            <div style={{ fontSize: 20, fontWeight: 600, color: '#ffffff', marginTop: 4 }}>
              {formatGal(leak.wasted_gal_7d)} / week
            </div>
          </div>
          <div style={{ borderRadius: 20, border: '1px solid rgba(251,113,133,0.15)', background: 'rgba(251,113,133,0.08)', padding: '14px 18px' }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: '#fda4af', textTransform: 'uppercase', letterSpacing: '0.1em' }}>
              Cost If Ignored
            </div>
            <div style={{ fontSize: 20, fontWeight: 600, color: '#ffffff', marginTop: 4 }}>
              {formatUsd(leak.cost_per_month)} / month
            </div>
          </div>
          <div style={{ borderRadius: 20, border: '1px solid rgba(103,232,249,0.15)', background: 'rgba(34,211,238,0.08)', padding: '14px 18px' }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: '#a5f3fc', textTransform: 'uppercase', letterSpacing: '0.1em' }}>
              Likely Fix
            </div>
            <div style={{ fontSize: 20, fontWeight: 600, color: '#ffffff', marginTop: 4 }}>
              Valve / Lateral Line
            </div>
          </div>
        </div>

        {zoneMetrics !== null && (() => {
          // A stuck-closed / no-flow valve can't be the cause of a continuous
          // background leak — only an overage (flowing more than expected)
          // anomaly is a plausible leak source, so "Likely zone" must only
          // ever list 'overage' anomalies, not 'no_flow' ones.
          const leakCandidates = (zoneMetrics.anomalies || []).filter((a) => a.kind === 'overage');
          return leakCandidates.length > 0 ? (
            <div style={{ fontSize: 13.5, color: '#fde68a', marginTop: 16 }}>
              Likely zone: {leakCandidates.map((a) => a.zone_name).join(', ')}
            </div>
          ) : (
            <div style={{ fontSize: 13.5, color: '#94a3b8', marginTop: 16 }}>
              Not attributable to a specific zone — constant flow while all zones are off.
            </div>
          );
        })()}
      </div>

      <ReferralBanner customerId={customerId} />

      {modalOpen && (
        <div
          role="presentation"
          onClick={() => setModalOpen(false)}
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(2,10,20,0.7)',
            backdropFilter: 'blur(6px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 50,
            padding: 20,
          }}
        >
          <div
            role="presentation"
            onClick={(e) => e.stopPropagation()}
            style={{
              background: 'rgba(6,18,36,0.97)',
              border: '1px solid rgba(255,255,255,0.12)',
              borderRadius: 24,
              padding: 28,
              maxWidth: 460,
              width: '100%',
              boxSizing: 'border-box',
              boxShadow: '0 30px 70px rgba(0,0,0,0.6)',
            }}
          >
            <div style={{ fontSize: 20, fontWeight: 600, color: '#ffffff' }}>Schedule a Service Trip</div>
            <div style={{ fontSize: 14, color: '#cbd5e1', marginTop: 8, lineHeight: 1.5 }}>
              {sevStyle.label.toLowerCase() === 'major leak' ? 'Major' : 'Possible'} leak, ~{leak.rate_gal_hr} gal/hr
              overnight. No trip charge for leak repairs found by the portal.
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 20 }}>
              {tripOpts.map((opt) => (
                <div
                  key={opt.dateLabel}
                  role="button"
                  tabIndex={0}
                  onClick={() => confirmTrip(opt.dateLabel, opt.isoDate, opt.window)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') confirmTrip(opt.dateLabel, opt.isoDate, opt.window);
                  }}
                  style={{
                    border: '1px solid rgba(255,255,255,0.1)',
                    background: 'rgba(255,255,255,0.03)',
                    borderRadius: 14,
                    padding: '14px 16px',
                    cursor: scheduling ? 'default' : 'pointer',
                    opacity: scheduling ? 0.6 : 1,
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                  }}
                >
                  <div style={{ fontSize: 14.5, fontWeight: 600, color: '#ffffff' }}>{opt.dateLabel}</div>
                  <div style={{ fontSize: 13, color: '#94a3b8' }}>{opt.window}</div>
                </div>
              ))}
            </div>
            {error && (
              <div style={{ color: 'rgba(255,120,120,0.9)', fontSize: '13px', lineHeight: 1.55, marginTop: 14 }}>
                {error}
              </div>
            )}
            <div style={{ display: 'flex', gap: 10, marginTop: 22 }}>
              <div
                role="button"
                tabIndex={0}
                onClick={() => setModalOpen(false)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') setModalOpen(false);
                }}
                style={{
                  flex: 1,
                  border: '1px solid rgba(255,255,255,0.15)',
                  borderRadius: 12,
                  padding: 13,
                  fontSize: 14.5,
                  fontWeight: 600,
                  textAlign: 'center',
                  cursor: 'pointer',
                  color: '#cbd5e1',
                }}
              >
                Cancel
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function ReferralModal({ customerId, onClose }) {
  const [copied, setCopied] = useState(false);
  const referralLink = `https://portal.aaairrigationservice.com/?ref=${customerId}`;
  const shareText = `Hey! I've been using AAA Irrigation's portal for smart watering — check it out: ${referralLink}`;

  const handleCopy = () => {
    navigator.clipboard.writeText(referralLink).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    });
  };

  const handleSMS = () => {
    window.open(`sms:?body=${encodeURIComponent(shareText)}`);
  };

  const handleEmail = () => {
    window.open(
      `mailto:?subject=${encodeURIComponent('Try AAA Irrigation’s Smart Portal')}&body=${encodeURIComponent(shareText)}`
    );
  };

  return (
    <div
      role="presentation"
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(2,10,20,0.7)',
        backdropFilter: 'blur(6px)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 50,
        padding: 20,
      }}
    >
      <div
        role="presentation"
        onClick={(e) => e.stopPropagation()}
        style={{
          background: 'rgba(6,18,36,0.97)',
          border: '1px solid rgba(255,255,255,0.12)',
          borderRadius: 24,
          padding: 28,
          maxWidth: 460,
          width: '100%',
          boxSizing: 'border-box',
          boxShadow: '0 30px 70px rgba(0,0,0,0.6)',
          position: 'relative',
        }}
      >
        <div
          role="button"
          tabIndex={0}
          onClick={onClose}
          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') onClose(); }}
          style={{
            position: 'absolute',
            top: 16,
            right: 20,
            fontSize: 20,
            color: '#94a3b8',
            cursor: 'pointer',
            lineHeight: 1,
            background: 'none',
            border: 'none',
            padding: '4px 6px',
          }}
        >
          ×
        </div>
        <div style={{ fontSize: 22, fontWeight: 700, color: '#ffffff', marginBottom: 8 }}>Refer a Friend</div>
        <div style={{ fontSize: 14, color: '#cbd5e1', lineHeight: 1.6, marginBottom: 20 }}>
          Love your service? Share AAA Irrigation with a friend — they get 10% off their first visit, and you get a $20 credit on your next invoice.
        </div>
        <div style={{ marginBottom: 16 }}>
          <div style={{ ...SECTION_LABEL_STYLE, marginBottom: 8 }}>Your referral link</div>
          <div style={{ display: 'flex', gap: 8 }}>
            <input
              readOnly
              value={referralLink}
              style={{
                flex: 1,
                background: 'rgba(255,255,255,0.06)',
                border: '1px solid rgba(255,255,255,0.12)',
                borderRadius: 10,
                padding: '10px 14px',
                fontSize: 13,
                color: '#e2e8f0',
                outline: 'none',
                boxSizing: 'border-box',
                minWidth: 0,
              }}
              onFocus={(e) => e.target.select()}
            />
            <div
              role="button"
              tabIndex={0}
              onClick={handleCopy}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') handleCopy(); }}
              style={{
                background: copied ? 'rgba(52,211,153,0.2)' : 'linear-gradient(90deg,#1094ff,#19d3b3)',
                color: '#fff',
                borderRadius: 10,
                padding: '10px 16px',
                fontWeight: 700,
                fontSize: 13,
                border: 'none',
                cursor: 'pointer',
                whiteSpace: 'nowrap',
                transition: 'background 0.2s',
              }}
            >
              {copied ? 'Copied! ✓' : 'Copy Link'}
            </div>
          </div>
        </div>
        <div style={{ display: 'flex', gap: 10, marginTop: 8, flexWrap: 'wrap' }}>
          <div
            role="button"
            tabIndex={0}
            onClick={handleSMS}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') handleSMS(); }}
            style={{
              flex: 1,
              background: 'linear-gradient(90deg,#1094ff,#19d3b3)',
              color: '#fff',
              borderRadius: 12,
              padding: '11px 20px',
              fontWeight: 700,
              fontSize: 14,
              border: 'none',
              cursor: 'pointer',
              textAlign: 'center',
              whiteSpace: 'nowrap',
            }}
          >
            Share via Text
          </div>
          <div
            role="button"
            tabIndex={0}
            onClick={handleEmail}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') handleEmail(); }}
            style={{
              flex: 1,
              background: 'linear-gradient(90deg,#1094ff,#19d3b3)',
              color: '#fff',
              borderRadius: 12,
              padding: '11px 20px',
              fontWeight: 700,
              fontSize: 14,
              border: 'none',
              cursor: 'pointer',
              textAlign: 'center',
              whiteSpace: 'nowrap',
            }}
          >
            Share via Email
          </div>
        </div>
        <div
          role="button"
          tabIndex={0}
          onClick={onClose}
          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') onClose(); }}
          style={{
            marginTop: 16,
            width: '100%',
            border: '1px solid rgba(255,255,255,0.15)',
            borderRadius: 12,
            padding: 13,
            fontSize: 14,
            fontWeight: 600,
            textAlign: 'center',
            cursor: 'pointer',
            color: '#cbd5e1',
            boxSizing: 'border-box',
          }}
        >
          Close
        </div>
      </div>
    </div>
  );
}

function ReferralCard({ customerId }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <div
        style={{
          borderRadius: 24,
          border: '1px solid rgba(110,231,183,0.15)',
          background: 'rgba(52,211,153,0.06)',
          padding: 22,
          boxSizing: 'border-box',
        }}
      >
        <div style={{ fontSize: 30, marginBottom: 10 }}>🎁</div>
        <div style={{ fontSize: 17, fontWeight: 700, color: '#ffffff', marginBottom: 6 }}>Refer a Friend</div>
        <div style={{ fontSize: 14, color: '#a7f3d0', lineHeight: 1.55, marginBottom: 18 }}>
          Get $20 credit when a friend books their first visit.
        </div>
        <div
          role="button"
          tabIndex={0}
          onClick={() => setOpen(true)}
          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') setOpen(true); }}
          style={{
            background: 'linear-gradient(90deg,#1094ff,#19d3b3)',
            color: '#fff',
            borderRadius: 12,
            padding: '11px 20px',
            fontWeight: 700,
            fontSize: 14,
            border: 'none',
            cursor: 'pointer',
            display: 'inline-block',
          }}
        >
          Share &amp; Earn $20
        </div>
      </div>
      {open && <ReferralModal customerId={customerId} onClose={() => setOpen(false)} />}
    </>
  );
}

function ReferralBanner({ customerId }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <div
        style={{
          borderRadius: 16,
          border: '1px solid rgba(110,231,183,0.25)',
          background: 'rgba(52,211,153,0.08)',
          padding: '16px 20px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: 14,
          boxSizing: 'border-box',
        }}
      >
        <div style={{ fontSize: 14, color: '#a7f3d0', lineHeight: 1.5, flex: 1, minWidth: 200 }}>
          🎁 <strong style={{ color: '#ffffff' }}>Refer a Friend — Earn $20 Credit</strong> · Share AAA Irrigation with a neighbor. They save 10% on their first visit.
        </div>
        <div
          role="button"
          tabIndex={0}
          onClick={() => setOpen(true)}
          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') setOpen(true); }}
          style={{
            background: 'linear-gradient(90deg,#1094ff,#19d3b3)',
            color: '#fff',
            borderRadius: 12,
            padding: '11px 20px',
            fontWeight: 700,
            fontSize: 14,
            border: 'none',
            cursor: 'pointer',
            whiteSpace: 'nowrap',
          }}
        >
          Share Now
        </div>
      </div>
      {open && <ReferralModal customerId={customerId} onClose={() => setOpen(false)} />}
    </>
  );
}

const PREF_DEFS = [
  ['sms', 'Text messages (SMS)', 'Urgent alerts sent to your phone'],
  ['email', 'Email', 'All alerts plus reports'],
  ['leak', 'Leak alerts', 'Unexplained flow detected on your meter'],
  ['tier', 'Tier warnings', "When you're within 3 days of the next price tier"],
  ['weekly', 'Weekly water report', 'Usage, savings and schedule summary every Monday'],
];

function SettingsTab({ customerId, onDisconnect }) {
  const [status, setStatus] = useState(null);
  const [prefs, setPrefs] = useState(null);
  const [savingKey, setSavingKey] = useState(null);
  const [disconnecting, setDisconnecting] = useState(false);
  const [error, setError] = useState(null);
  // contacts state
  const [contacts, setContacts] = useState([]);
  const [sendingCode, setSendingCode] = useState(null);
  const [verifyingCode, setVerifyingCode] = useState(null);
  const [codeInputs, setCodeInputs] = useState({});
  const [contactEdits, setContactEdits] = useState({});

  useEffect(() => {
    if (!customerId) return;
    fetch(`${BACKEND_URL}/api/watersmart/dashboard/${customerId}`).then((r) => r.json()).then(setStatus);
    fetch(`${BACKEND_URL}/api/watersmart/preferences/${customerId}`).then((r) => r.json()).then(setPrefs);
    fetch(`${BACKEND_URL}/api/watersmart/contacts/${customerId}`)
      .then((r) => r.json())
      .then((data) => setContacts(data.contacts || []))
      .catch(() => {});
  }, [customerId]);

  const togglePref = async (key) => {
    if (savingKey) return;
    const newValue = !prefs[key];
    setSavingKey(key);
    setError(null);
    // Confirm-first: don't flip the toggle until the backend confirms the save succeeded.
    try {
      const res = await fetch(`${BACKEND_URL}/api/watersmart/preferences`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ customer_id: customerId, key, value: newValue }),
      });
      const data = await res.json().catch(() => null);
      if (!res.ok || !data || !data.ok) {
        setError('Could not save that preference. Please try again.');
        return;
      }
      setPrefs((p) => ({ ...p, [key]: newValue }));
    } catch {
      setError('Could not reach the server. Please try again.');
    } finally {
      setSavingKey(null);
    }
  };

  const disconnect = async () => {
    if (disconnecting) return;
    setDisconnecting(true);
    setError(null);
    try {
      const res = await fetch(`${BACKEND_URL}/api/watersmart/disconnect`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ customer_id: customerId }),
      });
      const data = await res.json().catch(() => null);
      if (!res.ok || !data || !data.disconnected) {
        setError('Could not disconnect your account. Please try again.');
        return;
      }
      await onDisconnect();
    } catch {
      setError('Could not reach the server. Please try again.');
    } finally {
      setDisconnecting(false);
    }
  };

  if (!status || !prefs) return null;

  const syncedAt = status.synced_at ? new Date(status.synced_at) : null;

  return (
    <div style={{ marginTop: 24, display: 'flex', flexDirection: 'column', gap: 20, maxWidth: 720 }}>
      <div style={CARD_STYLE}>
        <div style={SECTION_LABEL_STYLE}>Water Account Connection</div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, marginTop: 16, flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{ width: 10, height: 10, borderRadius: '50%', background: '#34d399', flexShrink: 0 }} />
            <div>
              <div style={{ fontSize: 14.5, fontWeight: 600, color: '#ffffff' }}>City of Allen · WaterSmart — Connected</div>
              <div style={{ fontSize: 13, color: '#94a3b8', marginTop: 2 }}>
                {syncedAt ? `Last sync ${syncedAt.toLocaleString()}` : 'Sync pending'} · Credentials encrypted (AES-256)
              </div>
            </div>
          </div>
          <div
            role="button"
            tabIndex={0}
            onClick={disconnect}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') disconnect();
            }}
            style={{
              borderRadius: 12,
              border: '1px solid rgba(251,113,133,0.3)',
              padding: '9px 16px',
              fontSize: 13.5,
              fontWeight: 600,
              color: '#fda4af',
              cursor: disconnecting ? 'default' : 'pointer',
              opacity: disconnecting ? 0.6 : 1,
            }}
          >
            {disconnecting ? 'Disconnecting…' : 'Disconnect'}
          </div>
        </div>
      </div>

      <div style={CARD_STYLE}>
        {/* Header row with opt-out master toggle */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
          <div style={SECTION_LABEL_STYLE}>Notification Contacts</div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <span style={{ fontSize: 13, color: prefs && prefs.opt_out ? '#fda4af' : '#94a3b8' }}>
              Opt Out of All
            </span>
            <div
              role="switch"
              aria-checked={!!(prefs && prefs.opt_out)}
              aria-label="Opt out of all notifications"
              tabIndex={0}
              onClick={() => togglePref('opt_out')}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') togglePref('opt_out'); }}
              style={{
                width: 46,
                height: 27,
                borderRadius: 14,
                background: prefs && prefs.opt_out ? '#f87171' : 'rgba(148,163,184,0.35)',
                padding: 3,
                boxSizing: 'border-box',
                display: 'flex',
                justifyContent: prefs && prefs.opt_out ? 'flex-end' : 'flex-start',
                cursor: savingKey === 'opt_out' ? 'default' : 'pointer',
                opacity: savingKey === 'opt_out' ? 0.6 : 1,
                flexShrink: 0,
              }}
            >
              <div style={{ width: 21, height: 21, borderRadius: '50%', background: '#ffffff', boxShadow: '0 1px 3px rgba(0,0,0,0.35)' }} />
            </div>
          </div>
        </div>

        {/* Opt-out banner */}
        {prefs && prefs.opt_out && (
          <div
            style={{
              background: 'rgba(251,113,133,0.08)',
              border: '1px solid rgba(251,113,133,0.25)',
              borderRadius: 10,
              padding: '10px 14px',
              fontSize: 13.5,
              color: '#fda4af',
              marginBottom: 16,
            }}
          >
            All notifications paused.
          </div>
        )}

        {/* Contacts list */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 14 }}>
          {contacts.map((c) => {
            const isVerified = c.verified;
            const isPendingCode = verifyingCode === c.id;
            const chipLabel = isVerified ? 'VERIFIED' : isPendingCode ? 'PENDING CODE' : 'UNVERIFIED';
            const chipStyle = isVerified
              ? { background: 'rgba(52,211,153,0.15)', color: '#6ee7b7' }
              : isPendingCode
              ? { background: 'rgba(251,191,36,0.15)', color: '#fbbf24' }
              : { background: 'rgba(148,163,184,0.12)', color: '#94a3b8' };
            const editValue = contactEdits[c.id] !== undefined ? contactEdits[c.id] : c.value;
            const isDirty = contactEdits[c.id] !== undefined && contactEdits[c.id] !== c.value;

            return (
              <div key={c.id} style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                  <span style={{ fontSize: 16 }}>{c.type === 'phone' ? '📱' : '✉️'}</span>
                  <input
                    value={editValue}
                    onChange={(e) => setContactEdits((prev) => ({ ...prev, [c.id]: e.target.value }))}
                    onBlur={async () => {
                      if (!isDirty) return;
                      try {
                        const res = await fetch(`${BACKEND_URL}/api/watersmart/contacts/${c.id}`, {
                          method: 'PATCH',
                          headers: { 'Content-Type': 'application/json' },
                          body: JSON.stringify({ value: contactEdits[c.id] }),
                        });
                        const data = await res.json().catch(() => null);
                        if (res.ok && data && data.contact) {
                          setContacts((prev) => prev.map((x) => (x.id === c.id ? data.contact : x)));
                          setContactEdits((prev) => { const n = { ...prev }; delete n[c.id]; return n; });
                          setVerifyingCode(null);
                        }
                      } catch { /* ignore */ }
                    }}
                    style={{
                      flex: 1,
                      minWidth: 0,
                      background: 'rgba(255,255,255,0.06)',
                      border: '1px solid rgba(255,255,255,0.12)',
                      borderRadius: 8,
                      padding: '7px 10px',
                      fontSize: 13.5,
                      color: '#e2e8f0',
                      outline: 'none',
                      boxSizing: 'border-box',
                    }}
                  />
                  <span
                    style={{
                      ...chipStyle,
                      fontSize: 12,
                      fontWeight: 600,
                      borderRadius: 999,
                      padding: '3px 10px',
                      whiteSpace: 'nowrap',
                      flexShrink: 0,
                    }}
                  >
                    {chipLabel}
                  </span>
                  {!isVerified && (
                    <div
                      role="button"
                      tabIndex={0}
                      onClick={async () => {
                        if (sendingCode === c.id) return;
                        setSendingCode(c.id);
                        try {
                          const res = await fetch(`${BACKEND_URL}/api/watersmart/contacts/${c.id}/send-code`, {
                            method: 'POST',
                          });
                          if (res.ok) setVerifyingCode(c.id);
                        } catch { /* ignore */ } finally {
                          setSendingCode(null);
                        }
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          if (sendingCode !== c.id) {
                            setSendingCode(c.id);
                            fetch(`${BACKEND_URL}/api/watersmart/contacts/${c.id}/send-code`, { method: 'POST' })
                              .then((res) => { if (res.ok) setVerifyingCode(c.id); })
                              .catch(() => {})
                              .finally(() => setSendingCode(null));
                          }
                        }
                      }}
                      style={{
                        fontSize: 12.5,
                        fontWeight: 600,
                        color: '#60a5fa',
                        cursor: sendingCode === c.id ? 'default' : 'pointer',
                        opacity: sendingCode === c.id ? 0.6 : 1,
                        whiteSpace: 'nowrap',
                        padding: '4px 0',
                        flexShrink: 0,
                      }}
                    >
                      {sendingCode === c.id ? 'Sending…' : 'Send Code'}
                    </div>
                  )}
                  <div
                    role="button"
                    tabIndex={0}
                    onClick={async () => {
                      try {
                        const res = await fetch(`${BACKEND_URL}/api/watersmart/contacts/${c.id}`, { method: 'DELETE' });
                        if (res.ok) {
                          setContacts((prev) => prev.filter((x) => x.id !== c.id));
                          setContactEdits((prev) => { const n = { ...prev }; delete n[c.id]; return n; });
                          if (verifyingCode === c.id) setVerifyingCode(null);
                        }
                      } catch { /* ignore */ }
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        fetch(`${BACKEND_URL}/api/watersmart/contacts/${c.id}`, { method: 'DELETE' })
                          .then((res) => {
                            if (res.ok) {
                              setContacts((prev) => prev.filter((x) => x.id !== c.id));
                              setContactEdits((prev) => { const n = { ...prev }; delete n[c.id]; return n; });
                              if (verifyingCode === c.id) setVerifyingCode(null);
                            }
                          })
                          .catch(() => {});
                      }
                    }}
                    style={{
                      fontSize: 12.5,
                      fontWeight: 600,
                      color: '#fda4af',
                      cursor: 'pointer',
                      whiteSpace: 'nowrap',
                      padding: '4px 0',
                      flexShrink: 0,
                    }}
                  >
                    Remove
                  </div>
                </div>

                {/* Verification code input row */}
                {verifyingCode === c.id && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, paddingLeft: 24, flexWrap: 'wrap' }}>
                    <span style={{ fontSize: 13, color: '#94a3b8', whiteSpace: 'nowrap' }}>Code sent — enter it here:</span>
                    <input
                      value={codeInputs[c.id] || ''}
                      maxLength={6}
                      placeholder="6-digit code"
                      onChange={(e) => setCodeInputs((prev) => ({ ...prev, [c.id]: e.target.value.replace(/\D/g, '') }))}
                      style={{
                        width: 110,
                        background: 'rgba(255,255,255,0.06)',
                        border: '1px solid rgba(255,255,255,0.15)',
                        borderRadius: 8,
                        padding: '7px 10px',
                        fontSize: 14,
                        color: '#e2e8f0',
                        outline: 'none',
                        letterSpacing: 3,
                        boxSizing: 'border-box',
                      }}
                    />
                    <div
                      role="button"
                      tabIndex={0}
                      onClick={async () => {
                        const code = codeInputs[c.id] || '';
                        if (code.length < 6) return;
                        try {
                          const res = await fetch(`${BACKEND_URL}/api/watersmart/contacts/${c.id}/verify`, {
                            method: 'POST',
                            headers: { 'Content-Type': 'application/json' },
                            body: JSON.stringify({ code }),
                          });
                          const data = await res.json().catch(() => null);
                          if (data && data.verified) {
                            setContacts((prev) => prev.map((x) => (x.id === c.id ? { ...x, verified: true } : x)));
                            setVerifyingCode(null);
                            setCodeInputs((prev) => { const n = { ...prev }; delete n[c.id]; return n; });
                          }
                        } catch { /* ignore */ }
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          const code = codeInputs[c.id] || '';
                          if (code.length < 6) return;
                          fetch(`${BACKEND_URL}/api/watersmart/contacts/${c.id}/verify`, {
                            method: 'POST',
                            headers: { 'Content-Type': 'application/json' },
                            body: JSON.stringify({ code }),
                          })
                            .then((res) => res.json())
                            .then((data) => {
                              if (data && data.verified) {
                                setContacts((prev) => prev.map((x) => (x.id === c.id ? { ...x, verified: true } : x)));
                                setVerifyingCode(null);
                                setCodeInputs((prev) => { const n = { ...prev }; delete n[c.id]; return n; });
                              }
                            })
                            .catch(() => {});
                        }
                      }}
                      style={{
                        background: 'linear-gradient(90deg,#1094ff,#19d3b3)',
                        color: '#fff',
                        borderRadius: 8,
                        padding: '7px 14px',
                        fontSize: 13,
                        fontWeight: 600,
                        cursor: (codeInputs[c.id] || '').length < 6 ? 'default' : 'pointer',
                        opacity: (codeInputs[c.id] || '').length < 6 ? 0.5 : 1,
                        whiteSpace: 'nowrap',
                        border: 'none',
                      }}
                    >
                      Confirm
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Add contact buttons */}
        <div style={{ display: 'flex', gap: 10, marginBottom: 20, flexWrap: 'wrap' }}>
          {['phone', 'email'].map((type) => (
            <div
              key={type}
              role="button"
              tabIndex={0}
              onClick={async () => {
                try {
                  const res = await fetch(`${BACKEND_URL}/api/watersmart/contacts`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ customer_id: customerId, type, value: '' }),
                  });
                  const data = await res.json().catch(() => null);
                  if (res.ok && data && data.contact) {
                    setContacts((prev) => [...prev, data.contact]);
                    setContactEdits((prev) => ({ ...prev, [data.contact.id]: '' }));
                  }
                } catch { /* ignore */ }
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  fetch(`${BACKEND_URL}/api/watersmart/contacts`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ customer_id: customerId, type, value: '' }),
                  })
                    .then((res) => res.json())
                    .then((data) => {
                      if (data && data.contact) {
                        setContacts((prev) => [...prev, data.contact]);
                        setContactEdits((prev) => ({ ...prev, [data.contact.id]: '' }));
                      }
                    })
                    .catch(() => {});
                }
              }}
              style={{
                background: 'rgba(255,255,255,0.07)',
                border: '1px solid rgba(255,255,255,0.12)',
                borderRadius: 10,
                padding: '8px 16px',
                fontSize: 13,
                fontWeight: 600,
                color: '#94a3b8',
                cursor: 'pointer',
              }}
            >
              {type === 'phone' ? '+ Add Phone' : '+ Add Email'}
            </div>
          ))}
        </div>

        {/* Divider */}
        <div style={{ borderTop: '1px solid rgba(255,255,255,0.08)', marginBottom: 16 }} />

        {/* 5 preference toggles */}
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          {PREF_DEFS.map(([key, label, sub], i) => {
            const on = !!prefs[key];
            const dimmed = !!(prefs && prefs.opt_out);
            return (
              <div
                key={key}
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  gap: 14,
                  padding: '14px 0',
                  borderBottom: i < PREF_DEFS.length - 1 ? '1px solid rgba(255,255,255,0.08)' : 'none',
                  opacity: dimmed ? 0.4 : 1,
                  pointerEvents: dimmed ? 'none' : 'auto',
                }}
              >
                <div>
                  <div style={{ fontSize: 14.5, fontWeight: 600, color: '#ffffff' }}>{label}</div>
                  <div style={{ fontSize: 13, color: '#94a3b8', marginTop: 2 }}>{sub}</div>
                </div>
                <div
                  role="switch"
                  aria-checked={on}
                  aria-label={label}
                  tabIndex={0}
                  onClick={() => togglePref(key)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') togglePref(key);
                  }}
                  style={{
                    width: 46,
                    height: 27,
                    borderRadius: 14,
                    background: on ? '#19d3b3' : 'rgba(148,163,184,0.35)',
                    padding: 3,
                    boxSizing: 'border-box',
                    display: 'flex',
                    justifyContent: on ? 'flex-end' : 'flex-start',
                    cursor: savingKey === key ? 'default' : 'pointer',
                    opacity: savingKey === key ? 0.6 : 1,
                    flexShrink: 0,
                  }}
                >
                  <div style={{ width: 21, height: 21, borderRadius: '50%', background: '#ffffff', boxShadow: '0 1px 3px rgba(0,0,0,0.35)' }} />
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {error && (
        <div
          style={{
            fontSize: 13.5,
            color: '#fda4af',
            border: '1px solid rgba(251,113,133,0.15)',
            background: 'rgba(251,113,133,0.08)',
            borderRadius: 12,
            padding: '10px 14px',
          }}
        >
          {error}
        </div>
      )}
    </div>
  );
}

function ConnectScreen({ customerId, onConnected }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [consentAccepted, setConsentAccepted] = useState(false);
  const [consentText, setConsentText] = useState('');
  const [connecting, setConnecting] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetch(`${BACKEND_URL}/api/watersmart/consent-text`)
      .then((res) => res.json())
      .then((data) => setConsentText(data.text || ''))
      .catch(() => setConsentText(''));
  }, []);

  const doConnect = async () => {
    if (connecting) return;
    setConnecting(true);
    setError(null);
    try {
      const res = await fetch(`${BACKEND_URL}/api/watersmart/link`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ customer_id: customerId, email, password, consent_accepted: consentAccepted }),
      });
      const data = await res.json();
      if (data.connected) {
        onConnected();
      } else if (res.status === 401 || res.status === 403) {
        // Portal session missing/expired/mismatched — not a WaterSmart credential
        // problem. Surfacing the generic "check your email and password" message
        // here was a real bug: it told customers their WaterSmart login was wrong
        // when the actual cause was their portal session, misleading them into
        // retrying WaterSmart credentials that were never the problem.
        setError('Your portal session has expired. Please refresh the page and sign in again, then retry connecting WaterSmart.');
      } else {
        setError(data.error || data.detail || 'Could not connect — check your email and password.');
      }
    } catch {
      setError('Could not reach the server. Please try again.');
    } finally {
      setConnecting(false);
    }
  };

  // Visual layout ported verbatim from the mockup's notConnected block
  // (docs/superpowers/specs/assets/water-conservation-mockup.html lines 26-70).
  return (
    <div
      style={{
        minHeight: '100vh',
        background: PAGE_BG,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '32px 20px',
        boxSizing: 'border-box',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      <div
        style={{
          position: 'absolute',
          left: '-8%',
          top: '12%',
          height: '520px',
          width: '520px',
          borderRadius: '50%',
          background: 'rgba(34,211,238,0.10)',
          filter: 'blur(64px)',
          pointerEvents: 'none',
        }}
      />
      <div
        style={{
          position: 'absolute',
          right: '-10%',
          top: '8%',
          height: '520px',
          width: '520px',
          borderRadius: '50%',
          background: 'rgba(52,211,153,0.10)',
          filter: 'blur(64px)',
          pointerEvents: 'none',
        }}
      />
      <div
        style={{
          width: '100%',
          maxWidth: '440px',
          position: 'relative',
          background: 'rgba(6,18,36,0.92)',
          backdropFilter: 'blur(18px)',
          border: '1px solid rgba(0,224,184,0.22)',
          borderRadius: '20px',
          padding: '40px 36px 32px',
          boxSizing: 'border-box',
          boxShadow: '0 30px 70px rgba(0,0,0,0.6)',
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '18px' }}>
          <img
            src="/logo.png"
            alt="AAA Irrigation Service"
            style={{
              width: '80px',
              height: '80px',
              objectFit: 'contain',
              borderRadius: '50%',
              border: '2px solid rgba(255,255,255,0.2)',
              padding: '4px',
              boxSizing: 'border-box',
            }}
          />
        </div>
        <div
          style={{
            color: '#ffffff',
            fontSize: '22px',
            fontWeight: 900,
            fontStyle: 'italic',
            textTransform: 'uppercase',
            textAlign: 'center',
            letterSpacing: '1px',
          }}
        >
          Connect Your Water Account
        </div>
        <div
          style={{
            color: 'rgba(255,255,255,0.55)',
            fontSize: '11px',
            fontWeight: 700,
            textTransform: 'uppercase',
            letterSpacing: '4px',
            textAlign: 'center',
            marginTop: '4px',
            marginBottom: '24px',
          }}
        >
          City of Allen · WaterSmart
        </div>
        <div
          style={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: '10px',
            background: 'rgba(255,255,255,0.06)',
            border: '1px solid rgba(255,255,255,0.13)',
            borderRadius: '10px',
            padding: '12px 14px',
            marginBottom: '22px',
          }}
        >
          <div style={{ fontSize: '15px', lineHeight: 1 }}>🔒</div>
          {error ? (
            <div style={{ color: 'rgba(255,120,120,0.9)', fontSize: '13px', lineHeight: 1.55 }}>{error}</div>
          ) : (
            <div style={{ color: 'rgba(255,255,255,0.65)', fontSize: '13px', lineHeight: 1.55 }}>
              Your credentials are{' '}
              <strong style={{ color: 'rgba(255,255,255,0.88)', fontWeight: 600 }}>encrypted (AES-256)</strong> and
              used once a day to sync your smart-meter readings — never to make changes on your account.
            </div>
          )}
        </div>
        <div style={{ marginBottom: '16px' }}>
          <div
            style={{
              color: 'rgba(255,255,255,0.6)',
              fontSize: '11px',
              fontWeight: 700,
              letterSpacing: '2px',
              textTransform: 'uppercase',
              marginBottom: '8px',
            }}
          >
            WaterSmart Email
          </div>
          <div
            style={{
              background: 'rgba(255,255,255,0.03)',
              border: '1px solid rgba(255,255,255,0.08)',
              borderRadius: '14px',
              padding: '4px',
            }}
          >
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              style={{
                width: '100%',
                padding: '13px 14px',
                background: 'transparent',
                border: 'none',
                borderRadius: '10px',
                color: '#ffffff',
                fontSize: '15px',
                outline: 'none',
                boxSizing: 'border-box',
              }}
            />
          </div>
        </div>
        <div style={{ marginBottom: '16px' }}>
          <div
            style={{
              color: 'rgba(255,255,255,0.6)',
              fontSize: '11px',
              fontWeight: 700,
              letterSpacing: '2px',
              textTransform: 'uppercase',
              marginBottom: '8px',
            }}
          >
            WaterSmart Password
          </div>
          <div
            style={{
              background: 'rgba(255,255,255,0.03)',
              border: '1px solid rgba(255,255,255,0.08)',
              borderRadius: '14px',
              padding: '4px',
            }}
          >
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              style={{
                width: '100%',
                padding: '13px 14px',
                background: 'transparent',
                border: 'none',
                borderRadius: '10px',
                color: '#ffffff',
                fontSize: '15px',
                outline: 'none',
                boxSizing: 'border-box',
              }}
            />
          </div>
        </div>
        {consentText && (
          <div
            className="max-h-32 overflow-y-auto rounded-lg bg-white/5 p-3 text-xs text-slate-300 whitespace-pre-wrap"
            style={{ marginBottom: '16px' }}
          >
            {consentText}
          </div>
        )}
        <label
          style={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: '8px',
            color: 'rgba(255,255,255,0.65)',
            fontSize: '13px',
            lineHeight: 1.5,
            marginBottom: '20px',
            cursor: 'pointer',
          }}
        >
          <input
            type="checkbox"
            checked={consentAccepted}
            onChange={(e) => setConsentAccepted(e.target.checked)}
            style={{ marginTop: '2px' }}
          />
          <span>
            I authorize AAA Irrigation Service to retrieve my water usage data daily for leak detection and
            conservation insights.
          </span>
        </label>
        {connecting ? (
          <div
            style={{
              width: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '10px',
              padding: '15px',
              background: 'rgba(255,255,255,0.08)',
              color: '#ffffff',
              borderRadius: '12px',
              fontSize: '14px',
              fontWeight: 600,
              letterSpacing: '0.08em',
              textTransform: 'uppercase',
              boxSizing: 'border-box',
            }}
          >
            <div
              style={{
                width: '16px',
                height: '16px',
                border: '3px solid rgba(255,255,255,0.25)',
                borderTopColor: '#00e0b8',
                borderRadius: '50%',
                animation: 'spin 0.8s linear infinite',
              }}
            />
            Syncing meter data…
          </div>
        ) : (
          <div
            role="button"
            tabIndex={0}
            onClick={doConnect}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') doConnect();
            }}
            style={{
              width: '100%',
              padding: '15px',
              background: 'linear-gradient(90deg, #009dff, #00e0b8)',
              color: '#ffffff',
              borderRadius: '12px',
              fontSize: '14px',
              fontWeight: 600,
              letterSpacing: '0.08em',
              textTransform: 'uppercase',
              textAlign: 'center',
              cursor: 'pointer',
              boxShadow: '0 10px 25px rgba(0,200,180,0.25)',
              boxSizing: 'border-box',
            }}
          >
            Connect
          </div>
        )}
        <div style={{ marginTop: '28px', textAlign: 'center' }}>
          <div
            style={{
              width: '50%',
              margin: '0 auto 18px',
              height: '1px',
              background: 'linear-gradient(to right, transparent, rgba(0,255,180,0.3), transparent)',
            }}
          />
          <span style={{ color: 'rgba(255,255,255,0.4)', fontSize: '12px' }}>Powered by</span>
          <span
            style={{
              color: '#00e0b8',
              fontWeight: 600,
              letterSpacing: '0.08em',
              textShadow: '0 0 10px rgba(0,255,180,0.3)',
              marginLeft: '4px',
              fontSize: '12px',
            }}
          >
            Boatman Systems™
          </span>
        </div>
      </div>
    </div>
  );
}
